/* =====================================================
   NW IMMERSION — Product Page JavaScript
   Handles: Gallery, Accordion, FAQ, Qty, Swatches,
            Star Rating, Sliders, Scroll Animations
   ===================================================== */
'use strict';

/* ────────────────────────────────────────────────────
   GALLERY — main image/video swap + thumbnail scroller
   ──────────────────────────────────────────────────── */
class NwGallery {
  constructor(el) {
    this.el       = el;
    this.mainImg  = el.querySelector('.nw-pdp-gallery__main-img');
    this.mainVid  = el.querySelector('.nw-pdp-gallery__main-video');
    this.thumbs   = Array.from(el.querySelectorAll('.nw-pdp-gallery__thumb'));
    this.mobileThumbs = Array.from(document.querySelectorAll('.nw-pdp-mobile-thumb'));
    this.mobileImg = document.getElementById('nw-mobile-main-img');
    this.mobileVideo = document.getElementById('nw-mobile-main-video');
    this.mobileMediaWrap = document.querySelector('.nw-pdp-mobile-image');
    this.current  = 0;

    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.setActive(i));
    });

    // Mobile thumbnails
    this.mobileThumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.setActive(i));
    });

    el.querySelector('.nw-pdp-gallery__prev')?.addEventListener('click', () => this.prev());
    el.querySelector('.nw-pdp-gallery__next')?.addEventListener('click', () => this.next());

    // Mobile navigation
    document.querySelector('.nw-pdp-mobile-prev')?.addEventListener('click', () => this.prev());
    document.querySelector('.nw-pdp-mobile-next')?.addEventListener('click', () => this.next());

    // Zoom lightbox (only for images)
    el.querySelector('.nw-pdp-gallery__zoom')?.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.openLightbox();
    });

    // Click on main media area — images→zoom, videos→fullscreen play
    this.playOverlay = el.querySelector('.nw-pdp-gallery__play-overlay');
    this.mobilePlayOverlay = document.querySelector('.nw-pdp-mobile-play-overlay');

    el.querySelector('.nw-pdp-gallery__main')?.addEventListener('click', (e) => {
      e.preventDefault();
      if (e.target.closest('.nw-pdp-gallery__nav') || e.target.closest('.nw-pdp-gallery__zoom')) return;
      const thumb = this.thumbs[this.current];
      const mt = thumb?.dataset.mediaType || 'image';
      if (mt === 'video' || mt === 'external_video') {
        this.openVideoLightbox();
      } else {
        this.openLightbox();
      }
    });
    
    // Mobile image click → lightbox (only for images)
    if (this.mobileImg) {
      this.mobileImg.addEventListener('click', (e) => {
        e.preventDefault();
        const thumb = this.thumbs[this.current];
        const mt = thumb?.dataset.mediaType || 'image';
        if (mt === 'image') {
          this.openLightbox();
        } else if (mt === 'video' || mt === 'external_video') {
          this.openVideoLightbox();
        }
      });
    }

    // Touch Swipe for Mobile Image Container
    const mobileImgContainer = document.querySelector('.nw-pdp-mobile-image__container');
    if (mobileImgContainer) {
      let touchStartX = 0;
      let touchEndX = 0;
      
      mobileImgContainer.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });
      
      mobileImgContainer.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        const diff = touchStartX - touchEndX;
        if (Math.abs(diff) > 50) {
          if (diff > 0) this.next();
          else this.prev();
        }
      }, { passive: true });
    }

    // Mobile play overlay click → video popup
    this.mobilePlayOverlay?.addEventListener('click', () => {
      this.openVideoLightbox();
    });
    // Thumbnail scroll arrows
    this.thumbsContainer = el.querySelector('.nw-pdp-gallery__thumbs');
    this.arrowLeft  = el.querySelector('.nw-pdp-gallery__thumbs-arrow--left');
    this.arrowRight = el.querySelector('.nw-pdp-gallery__thumbs-arrow--right');
    if (this.thumbsContainer && this.arrowLeft && this.arrowRight) {
      this.arrowLeft.addEventListener('click', () => this._scrollThumbs(-1));
      this.arrowRight.addEventListener('click', () => this._scrollThumbs(1));
      this.thumbsContainer.addEventListener('scroll', () => this._updateArrows(), { passive: true });
      // Check on load and resize
      requestAnimationFrame(() => this._updateArrows());
      window.addEventListener('resize', () => this._updateArrows());
    }

    let initialIndex = 0;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const videoParam = urlParams.get('video');
      if (videoParam) {
        const paramIndex = this.thumbs.findIndex(t => t.dataset.imageId === videoParam || t.dataset.index === videoParam);
        if (paramIndex !== -1) {
          initialIndex = paramIndex;
        }
      }
    } catch (e) {
      console.warn('NW: Parsing video URL param failed', e);
    }

    if (this.thumbs.length) this.setActive(initialIndex, true);
  }

  setActive(i, skipFade) {
    this.thumbs.forEach(t => t.classList.remove('is-active'));
    this.mobileThumbs.forEach(t => t.classList.remove('is-active'));
    const thumb = this.thumbs[i];
    const mobileThumb = this.mobileThumbs[i];
    if (!thumb) return;
    thumb.classList.add('is-active');
    if (mobileThumb) mobileThumb.classList.add('is-active');
    this.current = i;

    const mediaType = thumb.dataset.mediaType || 'image';

    // Update shareable video URL parameter
    try {
      const url = new URL(window.location.href);
      if (mediaType === 'video' || mediaType === 'external_video') {
        const id = thumb.dataset.imageId || '';
        url.searchParams.set('video', id || i);
      } else {
        url.searchParams.delete('video');
      }
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      console.warn('NW: URL state update failed', e);
    }
    const isMobileView = this._isMobileView();

    if (mediaType === 'video' || mediaType === 'external_video') {
      /* -------------------------------------------------------
         Video media — show poster/preview image with play overlay.
         Clicking the overlay opens the video in a fullscreen popup.
         ------------------------------------------------------- */
      // Stop any playing video / iframe
      if (this.mainVid) { this.mainVid.pause(); this.mainVid.removeAttribute('src'); this.mainVid.classList.remove('is-active'); }
      const oldIframe = this.el.querySelector('.nw-pdp-gallery__main-iframe');
      if (oldIframe) { oldIframe.classList.remove('is-active'); oldIframe.src = ''; }

      // Show the thumbnail preview image as the main image (poster)
      const videoUrl = thumb.dataset.videoUrl;
      const posterSrc = thumb.querySelector('img')?.getAttribute('src');
      const posterAlt = thumb.querySelector('img')?.getAttribute('alt');
      let mobileInlineStarted = false;

      if (posterSrc) {
        if (this.mainImg) {
          this.mainImg.src = posterSrc;
          if (posterAlt) this.mainImg.alt = posterAlt;
        }
        if (this.mobileImg) {
          this.mobileImg.src = posterSrc;
          if (posterAlt) this.mobileImg.alt = posterAlt;
        }
      }

      if (mediaType === 'video' && videoUrl) {
        if (isMobileView) {
          if (this.mainImg) this.mainImg.classList.remove('is-hidden');
          mobileInlineStarted = this._startMobileVideo(videoUrl, posterSrc);
        } else if (this.mainVid) {
          if (this.mainImg) this.mainImg.classList.add('is-hidden');
          this._resetMobileVideo();
          this.mainVid.src = videoUrl;
          this.mainVid.muted = true;
          this.mainVid.autoplay = true;
          this.mainVid.loop = true;
          this.mainVid.playsInline = true;
          this.mainVid.controls = false;
          this.mainVid.classList.add('is-active');

          this.mainVid.load();
          this.mainVid.play().catch(function(err) {
            console.log('Autoplay blocked:', err);
          });
        }
      } else if (mediaType === 'external_video' && videoUrl) {
        const embedSrc = this._getExternalEmbedUrl(videoHost, videoUrl);
        const inlineIframe = this._getOrCreateExternalIframe();

        if (!isMobileView && embedSrc && inlineIframe) {
          if (this.mainImg) this.mainImg.classList.add('is-hidden');
          this._resetMobileVideo();
          inlineIframe.src = embedSrc;
          inlineIframe.classList.add('is-active');
        } else if (inlineIframe) {
          inlineIframe.classList.remove('is-active');
          inlineIframe.src = '';
          if (this.mainImg) this.mainImg.classList.remove('is-hidden');
        }
      } else {
        this._resetMobileVideo();
      }

      if (this.playOverlay) this.playOverlay.style.display = 'none';
      if (this.mobilePlayOverlay) {
        const showOverlay = isMobileView && (mediaType === 'external_video' || !mobileInlineStarted);
        this.mobilePlayOverlay.style.display = showOverlay ? 'flex' : 'none';
      }

    } else {
      // Image — hide video, hide play overlay, show image
      if (this.mainVid) {
        this.mainVid.classList.remove('is-active');
        this.mainVid.pause();
        this.mainVid.removeAttribute('src');
      }
      this._resetMobileVideo();
      // Hide any external video iframe
      const iframe = this.el.querySelector('.nw-pdp-gallery__main-iframe');
      if (iframe) { iframe.classList.remove('is-active'); iframe.src = ''; }
      // Hide play overlay
      if (this.playOverlay) this.playOverlay.style.display = 'none';
      if (this.mobilePlayOverlay) this.mobilePlayOverlay.style.display = 'none';
      if (this.mainImg) this.mainImg.classList.remove('is-hidden');
      const src = thumb.querySelector('img')?.getAttribute('src');
      if (src && this.mainImg) {
        if (skipFade) {
          this.mainImg.src = src;
          // Sync mobile duplicate image immediately
          if (this.mobileImg) {
            this.mobileImg.src = src;
            this.mobileImg.alt = this.mainImg.alt;
          }
          return;
        }
        this.mainImg.style.opacity = '0';
        setTimeout(() => {
          const hiResSrc = src.replace(/(_\d+x\d+)\./i, () => '.');
          this.mainImg.src = hiResSrc || src;
          this.mainImg.style.opacity = '1';
          // Sync mobile duplicate image after update
          if (this.mobileImg) {
            this.mobileImg.src = hiResSrc || src;
            this.mobileImg.alt = this.mainImg.alt;
          }
        }, 180);
      }
    }

    // Scroll active thumbnail into view
    if (this.thumbsContainer) {
      thumb.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }
    
    // Scroll active mobile thumbnail into view
    if (mobileThumb) {
      mobileThumb.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }
  }

  prev() {
    const n = this.thumbs.length;
    this.setActive((this.current - 1 + n) % n);
  }

  next() {
    const n = this.thumbs.length;
    this.setActive((this.current + 1) % n);
  }

  _scrollThumbs(dir) {
    if (!this.thumbsContainer) return;
    const scrollAmount = this.thumbsContainer.clientWidth * 0.6;
    this.thumbsContainer.scrollBy({ left: dir * scrollAmount, behavior: 'smooth' });
  }

  _updateArrows() {
    if (!this.thumbsContainer || !this.arrowLeft || !this.arrowRight) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.thumbsContainer;
    const canScrollLeft  = scrollLeft > 2;
    const canScrollRight = scrollLeft + clientWidth < scrollWidth - 2;
    this.arrowLeft.classList.toggle('is-visible', canScrollLeft);
    this.arrowRight.classList.toggle('is-visible', canScrollRight);
  }

  _isMobileView() {
    if (!this.mobileMediaWrap) return false;
    return getComputedStyle(this.mobileMediaWrap).display !== 'none';
  }

  _resetMobileVideo() {
    if (this.mobileVideo) {
      this.mobileVideo.pause();
      this.mobileVideo.removeAttribute('src');
      this.mobileVideo.load();
      this.mobileVideo.classList.remove('is-active');
    }
    if (this.mobileMediaWrap) {
      this.mobileMediaWrap.classList.remove('is-video-active');
    }
  }

  _startMobileVideo(videoUrl, posterSrc) {
    if (!this.mobileVideo || !videoUrl) return false;

    if (posterSrc) this.mobileVideo.setAttribute('poster', posterSrc);

    this.mobileVideo.src = videoUrl;
    this.mobileVideo.muted = true;
    this.mobileVideo.autoplay = true;
    this.mobileVideo.loop = true;
    this.mobileVideo.playsInline = true;
    this.mobileVideo.controls = false;
    this.mobileVideo.disablePictureInPicture = true;
    this.mobileVideo.controlsList = 'nodownload noplaybackrate';
    this.mobileVideo.setAttribute('controlsList', 'nodownload noplaybackrate');
    this.mobileVideo.setAttribute('disablepictureinpicture', '');
    this.mobileVideo.classList.add('is-active');
    this.mobileMediaWrap?.classList.add('is-video-active');

    this.mobileVideo.load();
    const playPromise = this.mobileVideo.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        this._resetMobileVideo();
        if (this.mobilePlayOverlay) this.mobilePlayOverlay.style.display = 'flex';
      });
    }

    return true;
  }

  _getOrCreateExternalIframe() {
    let iframe = this.el.querySelector('.nw-pdp-gallery__main-iframe');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.className = 'nw-pdp-gallery__main-iframe';
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
      this.el.querySelector('.nw-pdp-gallery__main')?.appendChild(iframe);
    }
    return iframe;
  }

  _getExternalEmbedUrl(videoHost, videoUrl) {
    if (!videoUrl) return '';
    if (videoHost === 'youtube') {
      return `https://www.youtube.com/embed/${videoUrl}?autoplay=1&mute=1&playsinline=1&rel=0`;
    }
    if (videoHost === 'vimeo') {
      return `https://player.vimeo.com/video/${videoUrl}?autoplay=1&muted=1&playsinline=1&background=1&autopause=0`;
    }
    return '';
  }

  openLightbox() {
    // Don't open lightbox for videos
    const thumb = this.thumbs[this.current];
    const mediaType = thumb?.dataset.mediaType || 'image';
    if (mediaType !== 'image') return;

    let lb = document.querySelector('.nw-gallery-lightbox');
    if (!lb) {
      lb = document.createElement('div');
      lb.className = 'nw-gallery-lightbox';
      lb.innerHTML = `
        <button class="nw-gallery-lightbox__close" aria-label="Close">&times;</button>
        <button class="nw-gallery-lightbox__prev" aria-label="Previous image">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <button class="nw-gallery-lightbox__next" aria-label="Next image">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
        <div class="nw-gallery-lightbox__spinner"></div>
        <img src="" alt="">
      `;
      document.body.appendChild(lb);
         
      // Close when clicking the dark backdrop outside the image.
      // We use 'click' (not pointerdown) to avoid a race condition where closing on
      // press-down causes the subsequent pointerup/click to land on an element below
      // and reopen the lightbox immediately.
      // The CSS rule  width:auto  on .nw-gallery-lightbox img ensures the img box
      // matches the actual rendered image size, so clicking any dark area around it
      // correctly targets lb (not img), making e.target===lb reliable.
      lb.addEventListener('click', (e) => {
        const t = /** @type {HTMLElement} */ (e.target);
        // Close button: always close
        if (t.closest('.nw-gallery-lightbox__close')) { lb.classList.remove('is-open'); return; }
        // Nav / spinner: never close
        if (t.closest('.nw-gallery-lightbox__prev') || t.closest('.nw-gallery-lightbox__next') || t.closest('.nw-gallery-lightbox__spinner')) return;
        // Dark backdrop: lb is the flex container, dark areas outside the image target it directly
        if (t === lb) {
          lb.classList.remove('is-open');
        }
      });
          
      // Navigation listeners
      const prevBtn = lb.querySelector('.nw-gallery-lightbox__prev');
      const nextBtn = lb.querySelector('.nw-gallery-lightbox__next');
      
      prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.lightboxPrevious();
      });
      
      nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.lightboxNext();
      });

      // Swipe navigation for lightbox on mobile
      let lbTouchStartX = 0;
      let lbTouchEndX = 0;
      lb.addEventListener('touchstart', (e) => {
        lbTouchStartX = e.changedTouches[0].screenX;
      }, { passive: true });
      
      lb.addEventListener('touchend', (e) => {
        lbTouchEndX = e.changedTouches[0].screenX;
        const diff = lbTouchStartX - lbTouchEndX;
        if (Math.abs(diff) > 50) {
          if (diff > 0) this.lightboxNext();
          else this.lightboxPrevious();
        }
      }, { passive: true });
      
      // Keyboard navigation
      document.addEventListener('keydown', (e) => {
        if (!lb.classList.contains('is-open')) return;
        if (e.key === 'ArrowLeft') this.lightboxPrevious();
        if (e.key === 'ArrowRight') this.lightboxNext();
        if (e.key === 'Escape') lb.classList.remove('is-open');
      });
    }
    
    this.updateLightboxImage();
    this.updateLightboxNavigation();
    lb.classList.add('is-open');
  }

  /* ── Lightbox navigation methods ── */
  lightboxPrevious() {
    const newIndex = this.current > 0 ? this.current - 1 : this.thumbs.length - 1;
    this.setActive(newIndex);
    this.handleLightboxMediaType();
  }

  lightboxNext() {
    const newIndex = this.current < this.thumbs.length - 1 ? this.current + 1 : 0;
    this.setActive(newIndex);
    this.handleLightboxMediaType();
  }

  handleLightboxMediaType() {
    const currentThumb = this.thumbs[this.current];
    const mediaType = currentThumb?.dataset.mediaType || 'image';
    
    if (mediaType === 'video' || mediaType === 'external_video') {
      // Close lightbox and open video popup for videos
      const lb = document.querySelector('.nw-gallery-lightbox');
      if (lb) lb.classList.remove('is-open');
      this.openVideoLightbox();
    } else {
      // Continue with image lightbox for images
      this.updateLightboxImage();
      this.updateLightboxNavigation();
    }
  }

  updateLightboxImage() {
    const lb = document.querySelector('.nw-gallery-lightbox');
    if (!lb) return;
    
    const img = lb.querySelector('img');
    const spinner = lb.querySelector('.nw-gallery-lightbox__spinner');
    const currentThumb = this.thumbs[this.current];
    
    if (img && currentThumb) {
      const fullSizeSrc = currentThumb.dataset.fullSize || currentThumb.querySelector('img')?.getAttribute('src');
      
      if (!fullSizeSrc) return;
      
      // Show loading state
      if (spinner) spinner.style.display = 'block';
      img.classList.add('is-loading');
      lb.classList.add('is-loading');
      
      // Preload the image
      const preloadImg = new Image();
      preloadImg.onload = () => {
        img.src = fullSizeSrc;
        img.classList.remove('is-loading');
        lb.classList.remove('is-loading');
        if (spinner) spinner.style.display = 'none';
      };
      preloadImg.onerror = () => {
        // Fallback: show image even if preload fails
        img.src = fullSizeSrc;
        img.classList.remove('is-loading');
        lb.classList.remove('is-loading');
        if (spinner) spinner.style.display = 'none';
      };
      preloadImg.src = fullSizeSrc;
    }
  }

  updateLightboxNavigation() {
    const lb = document.querySelector('.nw-gallery-lightbox');
    if (!lb) return;
    
    const prevBtn = lb.querySelector('.nw-gallery-lightbox__prev');
    const nextBtn = lb.querySelector('.nw-gallery-lightbox__next');
    
    // Hide navigation if only one image
    if (this.thumbs.length <= 1) {
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
    } else {
      prevBtn.style.display = '';
      nextBtn.style.display = '';
    }
  }
 

  /* ── Fullscreen video popup ── */
  openVideoLightbox() {
    const thumb = this.thumbs[this.current];
    if (!thumb) return;
    const mediaType = thumb.dataset.mediaType;
    const videoUrl  = thumb.dataset.videoUrl;
    const videoHost = thumb.dataset.videoHost;
    if (!videoUrl) return;

    // Create or reuse video-lightbox overlay
    let vl = document.querySelector('.nw-video-lightbox');
    if (!vl) {
      vl = document.createElement('div');
      vl.className = 'nw-video-lightbox';
      vl.innerHTML = `
        <button class="nw-video-lightbox__close" aria-label="Close">&times;</button>
        <button class="nw-video-lightbox__prev" aria-label="Previous media">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <button class="nw-video-lightbox__next" aria-label="Next media">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
        <div class="nw-video-lightbox__holder"></div>
      `;
      document.body.appendChild(vl);
      
      // Navigation listeners for video lightbox
      const prevBtn = vl.querySelector('.nw-video-lightbox__prev');
      const nextBtn = vl.querySelector('.nw-video-lightbox__next');
      
      prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.videoLightboxPrevious();
      });
      
      nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.videoLightboxNext();
      });
      
      // Close on backdrop or × click
      vl.addEventListener('click', (e) => {
        const t = /** @type {HTMLElement} */ (e.target);
        // Close on any click that is NOT on the video/iframe holder or nav buttons
        if (!t.closest('.nw-video-lightbox__holder') && !t.closest('.nw-video-lightbox__prev') && !t.closest('.nw-video-lightbox__next')) {
          vl.classList.remove('is-open');
          const h = vl.querySelector('.nw-video-lightbox__holder');
          if (h) h.innerHTML = '';
        }
      });
      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (!vl.classList.contains('is-open')) return;
        if (e.key === 'Escape') {
          vl.classList.remove('is-open');
          const h = vl.querySelector('.nw-video-lightbox__holder');
          if (h) h.innerHTML = '';
        }
        if (e.key === 'ArrowLeft') this.videoLightboxPrevious();
        if (e.key === 'ArrowRight') this.videoLightboxNext();
      });
    }

    const holder = vl.querySelector('.nw-video-lightbox__holder');
    holder.innerHTML = '';

    if (mediaType === 'video') {
      // Shopify-hosted video — <video> element
      const vid = document.createElement('video');
      vid.src = videoUrl;
      vid.controls = true;
      vid.autoplay = true;
      vid.playsInline = true;
      vid.style.cssText = 'max-width:100%;max-height:90vh;border-radius:8px;';
      holder.appendChild(vid);
    } else {
      // External (YouTube / Vimeo) — iframe
      const embedSrc = this._getExternalEmbedUrl(videoHost, videoUrl);
      if (embedSrc) {
        const iframe = document.createElement('iframe');
        iframe.src = embedSrc;
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
        iframe.style.cssText = 'width:min(90vw,1100px);height:min(70vh,620px);border:none;border-radius:8px;';
        holder.appendChild(iframe);
      }
    }

    vl.classList.add('is-open');
    this.updateVideoLightboxNavigation();
  }

  /* ── Video lightbox navigation methods ── */
  videoLightboxPrevious() {
    const newIndex = this.current > 0 ? this.current - 1 : this.thumbs.length - 1;
    this.setActive(newIndex);
    this.handleVideoLightboxMediaType();
  }

  videoLightboxNext() {
    const newIndex = this.current < this.thumbs.length - 1 ? this.current + 1 : 0;
    this.setActive(newIndex);
    this.handleVideoLightboxMediaType();
  }

  handleVideoLightboxMediaType() {
    const currentThumb = this.thumbs[this.current];
    const mediaType = currentThumb?.dataset.mediaType || 'image';
    
    if (mediaType === 'video' || mediaType === 'external_video') {
      // Update video content in video lightbox
      this.updateVideoContent();
      this.updateVideoLightboxNavigation();
    } else {
      // Close video lightbox and open image lightbox for images
      const vl = document.querySelector('.nw-video-lightbox');
      if (vl) {
        vl.classList.remove('is-open');
        const h = vl.querySelector('.nw-video-lightbox__holder');
        if (h) h.innerHTML = '';
      }
      this.openLightbox();
    }
  }

  updateVideoContent() {
    const vl = document.querySelector('.nw-video-lightbox');
    if (!vl) return;
    
    const thumb = this.thumbs[this.current];
    const mediaType = thumb?.dataset.mediaType;
    const videoUrl = thumb?.dataset.videoUrl;
    const videoHost = thumb?.dataset.videoHost;
    
    if (!videoUrl) return;
    
    const holder = vl.querySelector('.nw-video-lightbox__holder');
    holder.innerHTML = '';

    if (mediaType === 'video') {
      // Shopify-hosted video
      const vid = document.createElement('video');
      vid.src = videoUrl;
      vid.controls = true;
      vid.autoplay = true;
      vid.playsInline = true;
      vid.style.cssText = 'max-width:100%;max-height:90vh;border-radius:8px;';
      holder.appendChild(vid);
    } else {
      // External (YouTube / Vimeo)
      const embedSrc = this._getExternalEmbedUrl(videoHost, videoUrl);
      if (embedSrc) {
        const iframe = document.createElement('iframe');
        iframe.src = embedSrc;
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
        iframe.style.cssText = 'width:min(90vw,1100px);height:min(70vh,620px);border:none;border-radius:8px;';
        holder.appendChild(iframe);
      }
    }
  }

  updateVideoLightboxNavigation() {
    const vl = document.querySelector('.nw-video-lightbox');
    if (!vl) return;
    
    const prevBtn = vl.querySelector('.nw-video-lightbox__prev');
    const nextBtn = vl.querySelector('.nw-video-lightbox__next');
    
    // Hide navigation if only one media item
    if (this.thumbs.length <= 1) {
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
    } else {
      prevBtn.style.display = '';
      nextBtn.style.display = '';
    }
  }
}


/* ────────────────────────────────────────────────────
   ACCORDION — product info blocks
   ──────────────────────────────────────────────────── */
/**
 * @param {HTMLElement} item
 */
function scrollToAccordionItemTop(item) {
  if (!(item instanceof HTMLElement)) return;

  // Respect reduced motion and account for sticky header height.
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const headerOffset = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;
  const targetOffset = headerOffset + 12;

  // A small delay allows the accordion's opening animation to establish.
  setTimeout(() => {
    const targetY = item.getBoundingClientRect().top + window.scrollY - targetOffset;
    if (Math.abs(window.scrollY - targetY) <= 2) return;

    if (prefersReducedMotion) {
      window.scrollTo(0, targetY);
      return;
    }

    // Custom smooth scroll animation for precise control over speed and easing
    const startY = window.scrollY;
    const distance = targetY - startY;
    const duration = 600; // Animation duration in milliseconds (increase for slower scroll)
    let startTime = null;

    // Easing function (easeInOutCubic) for a professional feel
    function easeInOutCubic(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function smoothScroll(currentTime) {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      
      const ease = easeInOutCubic(progress);
      window.scrollTo(0, startY + (distance * ease));

      if (timeElapsed < duration) {
        requestAnimationFrame(smoothScroll);
      }
    }

    requestAnimationFrame(smoothScroll);
  }, 120);
}

/**
 * @param {HTMLElement} item
 * @param {string} contentSelector
 */
function closeAccordionItemInstantly(item, contentSelector) {
  if (!(item instanceof HTMLElement)) return;

  const content = item.querySelector(contentSelector);
  if (!(content instanceof HTMLElement)) {
    item.classList.remove('is-open');
    return;
  }

  const previousInlineTransition = content.style.transition;
  content.style.transition = 'none';
  item.classList.remove('is-open');
  // Force a style flush so the transition-less close state is committed.
  void content.offsetHeight;
  content.style.transition = previousInlineTransition;
}

class NwAccordion {
  constructor(el) {
    this.el    = el;
    this.el.style.overflowAnchor = 'none';
    this.items = Array.from(el.querySelectorAll('.nw-pdp-accordion__item'));

    this.items.forEach(item => {
      item.querySelector('.nw-pdp-accordion__trigger')
        ?.addEventListener('click', () => this.toggle(item));
    });
  }

  toggle(item) {
    const isOpen = item.classList.contains('is-open');
    if (isOpen) {
      item.classList.remove('is-open');
      return;
    }

    const currentOpen = this.items.find(i => i.classList.contains('is-open'));
    if (currentOpen && currentOpen !== item) {
      closeAccordionItemInstantly(currentOpen, '.nw-pdp-accordion__content');
    }

    this.items.forEach(i => {
      if (i !== item) i.classList.remove('is-open');
    });

    item.classList.add('is-open');
    scrollToAccordionItemTop(item);
  }
}

/**
 * Handles smooth scrolling and auto-expanding for all PDP jump links above the fold.
 */
function initResourcesJumpLink() {
  function handleJump(targetId, e) {
    if (!targetId) return;
    const cleanId = targetId.replace(/^#/, '');
    const target = document.getElementById(cleanId);
    if (!target) return; // Keep normal href behavior fallback if target not found

    if (e && e.preventDefault) {
      e.preventDefault();
    }

    const trigger = target.querySelector('.nw-pdp-accordion__trigger');
    const isAlreadyOpen = target.classList.contains('is-open');

    // Expand accordion if currently collapsed
    if (!isAlreadyOpen && trigger) {
      trigger.click();
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rootStyles = getComputedStyle(document.documentElement);
    const headerGroupHeight = parseFloat(rootStyles.getPropertyValue('--header-group-height')) ||
                              parseFloat(rootStyles.getPropertyValue('--header-height')) || 0;
    const stickyHeader = document.querySelector('.header, .nw-hdr__bar, [data-sticky-header]');
    const stickyHeight = stickyHeader ? stickyHeader.offsetHeight : 0;
    const headerOffset = Math.max(headerGroupHeight, stickyHeight, 60);
    const targetOffset = headerOffset + 24;

    setTimeout(() => {
      const targetY = target.getBoundingClientRect().top + window.scrollY - targetOffset;

      if (prefersReducedMotion) {
        window.scrollTo(0, targetY);
      } else {
        window.scrollTo({
          top: targetY,
          behavior: 'smooth'
        });
      }

      if (window.history && window.history.pushState) {
        window.history.pushState(null, null, '#' + cleanId);
      }

      if (trigger) {
        trigger.focus({ preventScroll: true });
      }
    }, isAlreadyOpen ? 10 : 140);
  }

  // Delegated click listener for all PDP jump links
  document.addEventListener('click', (e) => {
    const link = e.target.closest('.nw-pdp-jump-link, a[href="#resources-guides"], .nw-pdp-resources-jump');
    if (link) {
      const href = link.getAttribute('href');
      if (href && href.startsWith('#')) {
        handleJump(href, e);
      }
    }
  });

  // Handle direct hash navigation on page load
  if (window.location.hash) {
    const hash = window.location.hash;
    const validTargets = [
      '#electrical-requirements',
      '#specifications',
      '#whats-included',
      '#site-prep-installation',
      '#resources-guides',
      '#shipping-delivery',
      '#warranty',
      '#upgrades-add-ons'
    ];
    if (validTargets.includes(hash)) {
      setTimeout(() => {
        handleJump(hash, null);
      }, 400);
    }
  }

  // Hide any jump links whose destination accordion does not exist on this product
  function auditJumpLinks() {
    document.querySelectorAll('.nw-pdp-jump-link').forEach((link) => {
      const href = link.getAttribute('href');
      if (href && href.startsWith('#')) {
        const id = href.replace(/^#/, '');
        const target = document.getElementById(id);
        if (!target) {
          link.style.display = 'none';
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', auditJumpLinks);
  } else {
    auditJumpLinks();
  }
}

/**
 * Accordion metafield files (e.g. Manuals & Downloads diagrams): full-size
 * lightbox and download. Listeners are delegated from `document`, so they bind
 * once and keep working after theme-editor section re-renders. The lightbox root
 * deliberately does NOT carry `.nw-gallery-lightbox`: NwGallery looks that class
 * up with document.querySelector and would otherwise grab this overlay.
 */
class NwAccordionZoom {
  constructor() {
    if (NwAccordionZoom.bound) return;
    NwAccordionZoom.bound = true;

    this.triggers = [];
    this.index = 0;
    this.loadToken = 0;
    this.lb = null;

    document.addEventListener('click', (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;

      const downloadLink = target.closest('[data-nw-download]');
      if (downloadLink) {
        NwAccordionZoom.download(e, downloadLink);
        return;
      }

      const trigger = target.closest('.nw-pdp-accordion__split-images .nw-zoom-trigger');
      if (!trigger) return;
      // Modified clicks keep the link's native "open original in a new tab".
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();

      const group = trigger.closest('.nw-pdp-accordion__split-images');
      const triggers = Array.from(group.querySelectorAll('.nw-zoom-trigger'));
      this.open(triggers, Math.max(0, triggers.indexOf(trigger)));
    });

    document.addEventListener('keydown', (e) => {
      if (!this.lb || !this.lb.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      } else if (e.key === 'ArrowLeft' && this.triggers.length > 1) {
        this.step(-1);
      } else if (e.key === 'ArrowRight' && this.triggers.length > 1) {
        this.step(1);
      } else if (e.key === 'Tab') {
        this.trapFocus(e);
      }
    });
  }

  /**
   * Same-origin files download natively via the `download` attribute. Browsers
   * ignore that attribute cross-origin (cdn.shopify.com, preview domains), so
   * fetch the file instead (Shopify's CDN sends Access-Control-Allow-Origin: *)
   * and save the blob. If that fails, open the original file in a new tab.
   * @param {MouseEvent} e
   * @param {Element} link
   */
  static download(e, link) {
    const href = link.getAttribute('href');
    if (!href) return;

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch (err) {
      e.preventDefault();
      return;
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      e.preventDefault();
      return;
    }
    if (url.origin === window.location.origin) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    e.preventDefault();
    if (link.getAttribute('aria-busy') === 'true') return;
    link.setAttribute('aria-busy', 'true');

    const filename = link.getAttribute('download') ||
      decodeURIComponent(url.pathname.split('/').pop() || '') || 'download';

    fetch(url.href, { mode: 'cors', credentials: 'omit' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
      })
      .catch((err) => {
        console.warn('NW: Download failed, opening the original file instead', err);
        const win = window.open(url.href, '_blank');
        if (win) {
          win.opener = null;
        } else {
          window.location.href = url.href;
        }
      })
      .finally(() => link.removeAttribute('aria-busy'));
  }

  getLightbox() {
    if (this.lb && this.lb.isConnected) return this.lb;

    const lb = document.createElement('div');
    lb.className = 'nw-accordion-lightbox';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Full-size image');
    lb.setAttribute('aria-hidden', 'true');
    lb.innerHTML = `
      <button class="nw-gallery-lightbox__close" type="button" aria-label="Close">&times;</button>
      <button class="nw-gallery-lightbox__prev" type="button" aria-label="Previous image">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>
      <button class="nw-gallery-lightbox__next" type="button" aria-label="Next image">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
      <div class="nw-gallery-lightbox__spinner" aria-hidden="true"></div>
      <img class="nw-accordion-lightbox__img" src="" alt="">
      <p class="nw-accordion-lightbox__error" hidden>
        This image couldn't be displayed.
        <a href="" target="_blank" rel="noopener">Open the original file</a>
      </p>
      <a class="nw-accordion-lightbox__download" href="" target="_blank" rel="noopener" download data-nw-download>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
        <span>Download</span>
      </a>
    `;
    document.body.appendChild(lb);

    this.lb = lb;
    this.img = lb.querySelector('.nw-accordion-lightbox__img');
    this.spinner = lb.querySelector('.nw-gallery-lightbox__spinner');
    this.errorEl = lb.querySelector('.nw-accordion-lightbox__error');
    this.dlBtn = lb.querySelector('.nw-accordion-lightbox__download');
    this.closeBtn = lb.querySelector('.nw-gallery-lightbox__close');
    this.prevBtn = lb.querySelector('.nw-gallery-lightbox__prev');
    this.nextBtn = lb.querySelector('.nw-gallery-lightbox__next');

    // Backdrop (the dark area around the image) and the close button close it.
    lb.addEventListener('click', (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (t === lb || (t && t.closest('.nw-gallery-lightbox__close'))) this.close();
    });
    this.prevBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.step(-1);
    });
    this.nextBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.step(1);
    });

    let touchStartX = 0;
    lb.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    lb.addEventListener('touchend', (e) => {
      const diff = touchStartX - e.changedTouches[0].screenX;
      if (Math.abs(diff) > 50 && this.triggers.length > 1) this.step(diff > 0 ? 1 : -1);
    }, { passive: true });

    return lb;
  }

  open(triggers, index) {
    const lb = this.getLightbox();
    this.triggers = triggers;
    this.index = index;
    this.returnFocus = triggers[index] || null;
    this.show();
    lb.setAttribute('aria-hidden', 'false');
    lb.classList.add('is-open');
    this.closeBtn.focus({ preventScroll: true });
  }

  close() {
    if (!this.lb) return;
    this.loadToken++;
    this.lb.classList.remove('is-open', 'is-loading');
    this.lb.setAttribute('aria-hidden', 'true');
    if (this.returnFocus && this.returnFocus.isConnected) {
      this.returnFocus.focus({ preventScroll: true });
    }
  }

  step(delta) {
    const n = this.triggers.length;
    if (n < 2) return;
    this.index = (this.index + delta + n) % n;
    this.returnFocus = this.triggers[this.index];
    this.show();
  }

  show() {
    const trigger = this.triggers[this.index];
    if (!trigger) return;

    const url = trigger.getAttribute('data-zoom-img') || trigger.getAttribute('href') || '';
    const originalUrl = trigger.getAttribute('data-download-url') || url;
    const fileName = trigger.getAttribute('data-download-name') || '';
    const thumb = trigger.querySelector('img');
    const token = ++this.loadToken;
    const { lb, img, spinner, errorEl, dlBtn } = this;

    const finish = (ok) => {
      if (token !== this.loadToken) return;
      lb.classList.remove('is-loading');
      spinner.style.display = 'none';
      img.hidden = !ok;
      errorEl.hidden = ok;
    };

    lb.classList.add('is-loading');
    spinner.style.display = 'block';
    errorEl.hidden = true;
    errorEl.querySelector('a').href = originalUrl;
    img.hidden = false;
    img.alt = thumb ? thumb.getAttribute('alt') || '' : '';

    img.onload = () => finish(true);
    img.onerror = () => {
      if (token !== this.loadToken) return;
      // The sized URL failed: try the untouched original before giving up.
      if (originalUrl && img.getAttribute('src') !== originalUrl) {
        img.src = originalUrl;
        return;
      }
      finish(false);
    };
    if (url) {
      img.src = url;
      if (img.complete && img.naturalWidth > 0) finish(true);
    } else {
      finish(false);
    }

    if (originalUrl) {
      dlBtn.href = originalUrl;
      if (fileName) dlBtn.setAttribute('download', fileName);
      dlBtn.hidden = false;
    } else {
      dlBtn.hidden = true;
    }

    const multiple = this.triggers.length > 1;
    this.prevBtn.style.display = multiple ? '' : 'none';
    this.nextBtn.style.display = multiple ? '' : 'none';
  }

  /** @param {KeyboardEvent} e */
  trapFocus(e) {
    const focusable = Array.from(this.lb.querySelectorAll('button, a[href]'))
      .filter((el) => !el.hidden && el.offsetParent !== null && !el.closest('[hidden]'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    } else if (!this.lb.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    }
  }
}


/* ────────────────────────────────────────────────────
   QUANTITY SELECTOR
   ──────────────────────────────────────────────────── */
/**
 * Every `.nw-pdp-qty` on the page — the one in the ATC row and the one in the
 * sticky bar — shares a single quantity. Instances register here so a change in
 * one mirrors into the others instead of the two drifting apart.
 * @type {NwQty[]}
 */
const NW_QTY_INSTANCES = [];

class NwQty {
  constructor(el) {
    this.el       = el;
    this.display  = el.querySelector('.nw-pdp-qty__val');
    this.hidden   = document.getElementById('nw-qty-input');
    this.min      = parseInt(this.display?.dataset.min || '1');
    this.max      = parseInt(this.display?.dataset.max || '99');
    this.val      = this.min;

    el.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.change(-1));
    el.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.change(1));
    el.addEventListener('nw:quantity:set', (event) => {
      const requested = parseInt(event.detail?.quantity, 10);
      if (!Number.isFinite(requested)) return;
      this.set(requested);
    });

    // Lets the sticky bar detect that its picker is already wired up.
    el.dataset.nwQtyBound = 'true';
    NW_QTY_INSTANCES.push(this);
  }

  /**
   * @param {number} quantity
   * @param {boolean} [propagate] - false when called by syncAll, to avoid a loop.
   */
  set(quantity, propagate = true) {
    const next = Math.max(this.min, Math.min(quantity, this.max));
    this.val = next;
    if (this.display) this.display.textContent = this.val;
    if (this.hidden)  this.hidden.value = this.val;
    if (propagate) NwQty.syncAll(next, this);
  }

  change(delta) {
    const next = this.val + delta;
    if (next < this.min || next > this.max) return;
    this.set(next);
  }

  /**
   * @param {number} quantity
   * @param {NwQty} source
   */
  static syncAll(quantity, source) {
    NW_QTY_INSTANCES.forEach((instance) => {
      if (instance === source) return;
      instance.set(quantity, false);
    });
    document.dispatchEvent(new CustomEvent('nw:quantity:changed', { detail: { quantity } }));
  }
}

/* ────────────────────────────────────────────────────
   VARIANT SWATCH PICKER
   ──────────────────────────────────────────────────── */
function nwGetCurrentProductHandle() {
  const hiddenHandle = document.getElementById('nw-product-handle')?.value?.trim();
  if (hiddenHandle) return hiddenHandle.toLowerCase();

  const match = window.location.pathname.match(/\/products\/([^/?#]+)/i);
  return match ? match[1].toLowerCase() : '';
}

class NwVariantPicker {
  constructor(el) {
    this.formEl       = el;
    this.variantInput = document.getElementById('nw-variant-id');
    this.priceEl      = document.querySelector('.nw-pdp-price-current');
    this.compareEl    = document.querySelector('.nw-pdp-price-compare');
    this.saveEl       = document.querySelector('.nw-pdp-price-save');
    this.atcBtn       = document.querySelector('.nw-pdp-atc-btn');
    this.galleryEl    = document.querySelector('.nw-pdp-gallery');

    // Load variants JSON
    try {
      const raw = document.getElementById('nw-variants-json');
      this.variants = raw ? JSON.parse(raw.textContent) : [];
    } catch(e) {
      this.variants = [];
    }

    // Track selected options {position: value}
    this.selected = {};

    // Initialise with the first selected values
    el.querySelectorAll('.nw-pdp-swatch.is-selected').forEach(s => {
      const pos = s.dataset.optionPos;
      const val = s.dataset.optionVal;
      if (pos) this.selected[pos] = val;
    });

    // Attach events to all swatches
    el.querySelectorAll('.nw-pdp-swatch').forEach(sw => {
      sw.addEventListener('click', () => {
        const group = sw.closest('.nw-pdp-swatches');
        group?.querySelectorAll('.nw-pdp-swatch').forEach(s => s.classList.remove('is-selected'));
        sw.classList.add('is-selected');
        this.selected[sw.dataset.optionPos] = sw.dataset.optionVal;
        // Update the selected value label next to the option name
        const valLabel = this.formEl.querySelector('.nw-pdp-option__selected-val[data-option-pos="' + sw.dataset.optionPos + '"]');
        if (valLabel) {
          if (sw.dataset.optionVal === 'None') {
            valLabel.style.display = 'none';
          } else {
            valLabel.style.display = '';
            valLabel.textContent = sw.dataset.optionVal;
          }
        }
        this.syncVariant();
      });
    });

    this.syncVariant();
  }

  syncVariant() {
    // Match selected options to a variant
    const match = this.variants.find(v => {
      return Object.entries(this.selected).every(([pos, val]) => {
        const idx = parseInt(pos) - 1;
        return v.options[idx] === val;
      });
    });

    if (!match) return;

    // Update variant ID in form and dispatch change/custom variant change events
    if (this.variantInput) {
      this.variantInput.value = match.id;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      
      const variantEvent = new CustomEvent('variant:changed', {
        detail: { variant: match }
      });
      document.dispatchEvent(variantEvent);
    }

    document.querySelectorAll('[data-nw-payment-terms-variant]').forEach(input => {
      input.value = match.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    document.querySelectorAll('shopify-payment-terms').forEach(paymentTerms => {
      paymentTerms.setAttribute('variant-id', match.id);
      if ('variantId' in paymentTerms) {
        paymentTerms.variantId = match.id;
      }
    });

    // Update price with comma formatting and two decimal places
    const fmt = (cents) => {
      // Convert to a number, then format it to two decimal places
      const amount = (cents / 100).toFixed(2); // Always two decimal places
      const parts = amount.split('.'); // Split the amount into integer and decimal parts

      // Add commas to the integer part (before the decimal)
      const integerPart = Number(parts[0]).toLocaleString();

      // Return the formatted amount with the decimal part
      return window.Shopify?.currency?.active === 'USD'
        ? `$${integerPart}.${parts[1]}`
        : `$${integerPart}.${parts[1]}`;
    };

    // Check if the price element exists and match.price is available
    if (this.priceEl && match.price) {
      this.priceEl.textContent = fmt(match.price);
    }

    // Check if the compare price element exists and match.compare_at_price is available
    if (this.compareEl) {
      const hasCompare = match.compare_at_price && match.compare_at_price > match.price;
      this.compareEl.textContent = hasCompare ? fmt(match.compare_at_price) : '';
      this.compareEl.style.display = hasCompare ? '' : 'none';
    }

    // Check if the save element exists and there are savings
    if (this.saveEl) {
      const hasSavings = match.compare_at_price && match.compare_at_price > match.price;
      this.saveEl.textContent = hasSavings ? `SAVE ${fmt(match.compare_at_price - match.price)}` : '';
      this.saveEl.style.display = hasSavings ? '' : 'none';
    }

    // Update SKU value dynamically
    const skuValEl = document.getElementById('nw-sku-val');
    if (skuValEl) {
      skuValEl.textContent = match.sku || '';
      const skuContainer = skuValEl.closest('.nw-pdp-sku');
      if (skuContainer) {
        skuContainer.style.display = match.sku ? '' : 'none';
      }
    }

    // Dynamic stock/reservation tracking for Sauna Elite red light panels only
    const handle = nwGetCurrentProductHandle();
    const isSaunaElitePanel = handle === 'sauna-elite-red-light-therapy-panel';
    let threshold = -1;

    const standaloneChillers = [
      'nw-immersion-elite-2hp-ice-bath-chiller',
      'nw-immersion-elite-pro-chiller-1hp-37-107',
      'nw-immersion-0-3hp-cold-plunge-chiller',
      'nw-immersion-stainless-solo-cold-tub',
      'immersion-tub-luxury-hot-cold-plunge'
    ];
    const isStandaloneChiller = standaloneChillers.indexOf(handle) !== -1;

    if (!isStandaloneChiller) {
      if (handle === 'sauna-elite-red-light-therapy-panel') {
        threshold = 25;
      }
    }

    let isZeroInventory = false;
    const productTitle = document.querySelector('.nw-pdp-title')?.innerText || '';

    // Check if standalone chiller is in Reserve Now state
    let isReserveNowChiller = false;
    if (isStandaloneChiller && match) {
      const inventoryQty = match.inventory_quantity;
      const hasInventoryQty = inventoryQty !== undefined && inventoryQty !== null && !isNaN(inventoryQty);
      const hasTrackedInventory = match.inventory_management !== undefined
        && match.inventory_management !== null
        && match.inventory_management !== '';
      if (hasTrackedInventory && hasInventoryQty && inventoryQty <= 0 && match.inventory_policy === 'continue') {
        isReserveNowChiller = true;
      }
    }

    if (threshold !== -1 && match) {
      const inventoryQty = match.inventory_quantity;
      const hasInventoryQty = inventoryQty !== undefined && inventoryQty !== null && !isNaN(inventoryQty);
      const hasTrackedInventory = match.inventory_management !== undefined
        && match.inventory_management !== null
        && match.inventory_management !== '';
      const msgContainer = isSaunaElitePanel
        ? document.querySelector('[data-sauna-elite-inventory-message]')
        : document.getElementById('nw-pdp-inventory-msg');
      const qtyPicker = document.querySelector('.nw-pdp-qty');
      const nextProductionMonth = msgContainer?.dataset.nextProductionMonth || '';
      // Set from Theme settings > NW Availability Messaging via data-reserve-eta.
      const reserveEta = msgContainer?.dataset.reserveEta || 'end of September';
      
      let msgHtml = '';
      
      if (hasInventoryQty && (!isSaunaElitePanel || hasTrackedInventory)) {
        if (inventoryQty <= 0) {
          isZeroInventory = true;
          const isOct2026OrLater = msgContainer?.dataset.isOct2026 === 'true';
          if (isSaunaElitePanel && isOct2026OrLater) {
            msgHtml = `
              <div class="nw-pdp-inventory-alert zero-inventory">
                <strong>Reserve Your NW Immersion Sauna Elite Near Infrared & Red Light Therapy Panel</strong> — Our current production run is fully reserved. Reserve yours today to secure your place in the next available production run. Orders are estimated to ship within 4 weeks or sooner. Questions about availability or timing? Call or text 360-360-6968 — our wellness specialists are happy to help.
              </div>`;
          } else {
            msgHtml = `
              <div class="nw-pdp-inventory-alert zero-inventory">
                <strong>Reserve Your ${productTitle}</strong> — Our current production run has been fully reserved. Reserve yours today to secure your place in the next production run, with estimated delivery by ${reserveEta}. Questions about timing? Call or text 360-360-6968 — our wellness specialists are happy to help.
              </div>`;
          }
        } else if (inventoryQty <= threshold) {
          if (isSaunaElitePanel) {
            const panelLabel = inventoryQty === 1 ? 'Panel' : 'Panels';
            const inventoryVerb = inventoryQty === 1 ? 'remains' : 'remain';
            msgHtml = `
              <div class="nw-pdp-inventory-alert low-inventory">
                <strong>Current Production Nearly Reserved — Only ${inventoryQty} Sauna Elite ${panelLabel} Available</strong><br>
                Only ${inventoryQty} NW Immersion Sauna Elite Red + Near-Infrared Light ${panelLabel} ${inventoryVerb} available from our current production run. Reserve yours today to secure the earliest available delivery.<br>
                Once this batch is sold out, new orders will ship with our next production run, currently expected to begin shipping in ${nextProductionMonth}.
              </div>`;
          } else {
            msgHtml = `
              <div class="nw-pdp-inventory-alert low-inventory">
                <strong>Current Production Nearly Reserved</strong> — Only ${inventoryQty} ${productTitle} remain available from our current production run. Reserve yours today to receive the earliest available delivery.
              </div>`;
          }
        }
      }
      
      if (msgContainer) {
        msgContainer.innerHTML = msgHtml;
        if (isSaunaElitePanel) {
          msgContainer.hidden = msgHtml === '';
        }
      }
      
      if (qtyPicker) {
        qtyPicker.style.display = isZeroInventory ? 'none' : '';
      }
      const atcRow = this.form ? this.form.querySelector('.nw-pdp-atc-row') : null;
      if (atcRow) {
        atcRow.style.gridTemplateColumns = (threshold !== -1 && isZeroInventory) ? '1fr' : '';
      }
    }

    // Update quantity picker & grid for standalone chillers
    const qtyPicker = document.querySelector('.nw-pdp-qty');
    const atcRow = document.querySelector('.nw-pdp-atc-row');
    if (isStandaloneChiller) {
      if (qtyPicker) {
        qtyPicker.style.display = isReserveNowChiller ? 'none' : '';
      }
      if (atcRow) {
        atcRow.style.gridTemplateColumns = isReserveNowChiller ? '1fr' : '';
      }
    }

    // Update ATC button
    if (this.atcBtn) {
      if (isStandaloneChiller) {
        this.atcBtn.classList.remove('klaviyo_form_trigger');
        this.atcBtn.removeAttribute('data-klaviyo-form-id');
        this.atcBtn.setAttribute('type', 'submit');

        if (isReserveNowChiller) {
          this.atcBtn.disabled = false; // Always enabled for reservation purchase!

          // Set text + price span
          const baseText = 'Reserve Now';
          this.atcBtn.textContent = '';
          this.atcBtn.append(baseText);
          
          let atcPriceSpan = this.atcBtn.querySelector('[data-nw-atc-price]');
          if (!atcPriceSpan) {
            atcPriceSpan = document.createElement('span');
            atcPriceSpan.className = 'nw-pdp-atc-price';
            atcPriceSpan.setAttribute('data-nw-atc-price', '');
            this.atcBtn.appendChild(atcPriceSpan);
          }
          atcPriceSpan.innerHTML = ' <span class="nw-pdp-atc-sep"> &middot;</span> ' + fmt(match.price);
        } else {
          const avail = match.available;
          this.atcBtn.disabled = !avail;

          // Preserve the price <span> inside the button
          const atcPriceSpan = this.atcBtn.querySelector('[data-nw-atc-price]');
          if (avail) {
            // Keep text + price span
            const baseText = this.atcBtn.getAttribute('data-original-atc-text') || 'Add to Cart';
            this.atcBtn.textContent = '';
            this.atcBtn.append(baseText);
            if (atcPriceSpan) {
              this.atcBtn.appendChild(atcPriceSpan);
              // Update the price with the new variant price
              atcPriceSpan.innerHTML = ' <span class="nw-pdp-atc-sep"> &middot;</span> ' + fmt(match.price);
            }
          } else {
            this.atcBtn.textContent = 'Sold Out';
          }
        }
      } else if (threshold !== -1) {
        this.atcBtn.classList.remove('klaviyo_form_trigger');
        this.atcBtn.removeAttribute('data-klaviyo-form-id');
        this.atcBtn.setAttribute('type', 'submit');

        if (isZeroInventory) {
          this.atcBtn.disabled = false; // Always enabled for reservation purchase!

          // Set text + price span
          const baseText = 'Reserve Now';
          this.atcBtn.textContent = '';
          this.atcBtn.append(baseText);
          
          let atcPriceSpan = this.atcBtn.querySelector('[data-nw-atc-price]');
          if (!atcPriceSpan) {
            atcPriceSpan = document.createElement('span');
            atcPriceSpan.className = 'nw-pdp-atc-price';
            atcPriceSpan.setAttribute('data-nw-atc-price', '');
            this.atcBtn.appendChild(atcPriceSpan);
          }
          atcPriceSpan.innerHTML = ' <span class="nw-pdp-atc-sep"> &middot;</span> ' + fmt(match.price);
        } else {
          const avail = match.available;
          this.atcBtn.disabled = !avail;

          // Preserve the price <span> inside the button
          const atcPriceSpan = this.atcBtn.querySelector('[data-nw-atc-price]');
          if (avail) {
            // Keep text + price span
            const baseText = this.atcBtn.getAttribute('data-original-atc-text') || this.atcBtn.getAttribute('data-base-text') || 'Add to Cart';
            this.atcBtn.textContent = '';
            this.atcBtn.append(baseText);
            if (atcPriceSpan) {
              this.atcBtn.appendChild(atcPriceSpan);
              // Update the price with the new variant price (addon picker will re-run too)
              atcPriceSpan.innerHTML = ' <span class="nw-pdp-atc-sep"> &middot;</span> ' + fmt(match.price);
            }
          } else {
            this.atcBtn.textContent = 'Sold Out';
          }
        }
      } else {
        // Original logic for standard and preorder products
        const avail = match.available;
        this.atcBtn.disabled = !avail;

        // Preserve the price <span> inside the button
        const atcPriceSpan = this.atcBtn.querySelector('[data-nw-atc-price]');
        if (avail) {
          // Keep text + price span
          const baseText = this.atcBtn.getAttribute('data-original-atc-text') || this.atcBtn.getAttribute('data-base-text') || 'Add to Cart';
          this.atcBtn.textContent = '';
          this.atcBtn.append(baseText);
          if (atcPriceSpan) {
            this.atcBtn.appendChild(atcPriceSpan);
            // Update the price with the new variant price (addon picker will re-run too)
            atcPriceSpan.innerHTML = ' <span class="nw-pdp-atc-sep"> &middot;</span> ' + fmt(match.price);
          }
        } else {
          this.atcBtn.textContent = 'Sold Out';
        }
      }
    }

    // Update gallery to reflect the selected variant
    if (this.galleryEl) {
      const gallery = this.galleryEl._nwGalleryInstance;
      if (gallery) {
        if (match.featured_image) {
          // Match thumbnail by image ID (data-image-id = media.preview_image.id in Liquid,
          // which matches variant.featured_image.id from the variants JSON)
          const thumbIdx = gallery.thumbs.findIndex(t =>
            t.dataset.imageId && t.dataset.imageId === String(match.featured_image.id)
          );
          if (thumbIdx > -1) {
            // setActive() updates both desktop gallery AND mobileImg src internally
            gallery.setActive(thumbIdx);
          } else {
            // No matching gallery slide — just clear any video state so the
            // currently displayed image stays visible (do NOT overwrite mobileImg.src)
            gallery._resetMobileVideo();
          }
        } else {
          // Variant has no featured image — keep current image, just clear video state
          gallery._resetMobileVideo();
        }
      }
    }

    // Update financing price if there is no addon container
    if (this.formEl && !this.formEl.querySelector('[data-nw-addons]') && match) {
      if (window.nwAffirmController) {
        window.nwAffirmController.updatePrice(match.price);
      }
      if (window.nwShopPayController) {
        window.nwShopPayController.updatePrice(match.price, match.price);
      }
    }
  }
}

/* ────────────────────────────────────────────────────
   STAR RATING (review form)
   ──────────────────────────────────────────────────── */
class NwStarRating {
  constructor(el) {
    this.stars  = Array.from(el.querySelectorAll('.nw-pdp-star-rating-display svg'));
    this.input  = el.querySelector('[name="rating"]');
    this.rating = 0;

    this.stars.forEach((star, i) => {
      star.addEventListener('mouseenter', () => this.highlight(i + 1));
      star.addEventListener('mouseleave', () => this.highlight(this.rating));
      star.addEventListener('click',      () => this.select(i + 1));
    });
  }

  highlight(n) {
    this.stars.forEach((s, i) => {
      s.classList.toggle('is-hover',   i < n && !this.rating);
      s.classList.toggle('is-active',  i < this.rating);
    });
    // On hover, colour up to n
    this.stars.forEach((s, i) => {
      s.classList.toggle('is-hover',  i < n);
    });
  }

  select(n) {
    this.rating = n;
    this.stars.forEach((s, i) => {
      s.classList.toggle('is-active', i < n);
      s.classList.remove('is-hover');
    });
    if (this.input) this.input.value = n;
  }
}

/* ────────────────────────────────────────────────────
   STORIES SLIDER (testimonials prev/next)
   ──────────────────────────────────────────────────── */
class NwStoriesSlider {
  constructor(el) {
    this.el    = el;
    this.cards = Array.from(el.querySelectorAll('.nw-pdp-review-card'));
    this.idx   = 0;

    el.querySelector('[data-stories-prev]')?.addEventListener('click', () => this.go(-1));
    el.querySelector('[data-stories-next]')?.addEventListener('click', () => this.go(1));

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.render(), 150);
    });

    this.render();
  }

  perPage() {
    if (window.innerWidth < 768)  return 1;
    if (window.innerWidth < 1200) return 2;
    return 3;
  }

  go(dir) {
    const pp  = this.perPage();
    const max = Math.ceil(this.cards.length / pp) - 1;
    this.idx  = Math.max(0, Math.min(this.idx + dir, max));
    this.render();
  }

  render() {
    const pp    = this.perPage();
    const start = this.idx * pp;
    this.cards.forEach((card, i) => {
      card.style.display = (i >= start && i < start + pp) ? '' : 'none';
    });
  }
}

/* ────────────────────────────────────────────────────
   RELATED PRODUCTS SLIDER
   ──────────────────────────────────────────────────── */
class NwRelatedSlider {
  constructor(el) {
    this.el    = el;
    this.cards = Array.from(el.querySelectorAll('.nw-pdp-product-card'));
    this.idx   = 0;

    el.querySelector('[data-related-prev]')?.addEventListener('click', () => this.go(-1));
    el.querySelector('[data-related-next]')?.addEventListener('click', () => this.go(1));

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.render(), 150);
    });

    this.render();
  }

  perPage() {
    if (window.innerWidth < 768)  return 1;
    if (window.innerWidth < 1200) return 2;
    return 3;
  }

  go(dir) {
    const pp  = this.perPage();
    const max = Math.ceil(this.cards.length / pp) - 1;
    this.idx  = Math.max(0, Math.min(this.idx + dir, max));
    this.render();
  }

  render() {
    const pp    = this.perPage();
    const start = this.idx * pp;
    this.cards.forEach((card, i) => {
      card.style.display = (i >= start && i < start + pp) ? '' : 'none';
    });
  }
}

/* ────────────────────────────────────────────────────
   SCROLL FADE-UP ANIMATIONS (IntersectionObserver)
   Progressive-enhancement: elements are visible by
   default.  We add .nw-anim-ready to <body> first so
   CSS can hide them, then observe with IO.
   If this class never runs, content stays visible.
   ──────────────────────────────────────────────────── */
class NwScrollAnim {
  constructor() {
    const els = document.querySelectorAll('.nw-fade-up');
    if (!els.length) return;

    // Signal that JS is ready — CSS will now hide .nw-fade-up
    document.body.classList.add('nw-anim-ready');

    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -20px 0px' });

    els.forEach(el => obs.observe(el));
  }
}

/* ────────────────────────────────────────────────────
   ADD TO CART — AJAX submission with feedback
   Adds main product + any selected add-on products
   as separate cart items so each has its own price.
   ──────────────────────────────────────────────────── */
function nwClampAddonDiscountPct(value) {
  const pct = Number.parseFloat(String(value ?? '').trim());
  if (!Number.isFinite(pct) || pct <= 0) return 0;
  return Math.min(pct, 100);
}

function nwFormatAddonDiscountPct(value) {
  const pct = nwClampAddonDiscountPct(value);
  return Number.isInteger(pct) ? String(pct) : String(Number(pct.toFixed(2)));
}

function nwCartEndpoint(path) {
  const root = window.Shopify?.routes?.root || '/';
  return root.replace(/\/$/, '/') + String(path || '').replace(/^\//, '');
}

function nwGetCartEditKey() {
  try {
    return new URLSearchParams(window.location.search).get('nw_cart_edit') || '';
  } catch (_) {
    return '';
  }
}

function nwGetReadyCartEditState() {
  const state = window.NW_CART_EDIT_STATE;
  if (!state || !state.ready || !Array.isArray(state.keys) || !state.keys.length) return null;
  return state;
}

function nwCollectCartPayloadKeys(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.items)) {
    return payload.items.map(item => item && item.key).filter(Boolean);
  }
  return payload.key ? [payload.key] : [];
}

async function nwUpdateCartKeysToZero(keys) {
  const cleanKeys = Array.from(new Set((keys || []).filter(Boolean)));
  if (!cleanKeys.length) return null;

  const updates = {};
  cleanKeys.forEach(key => {
    updates[key] = 0;
  });

  const res = await fetch(nwCartEndpoint('cart/update.js'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates })
  });

  if (!res.ok) {
    throw new Error('Unable to update edited cart items');
  }

  return res.json().catch(() => null);
}

async function nwRollbackAddedCartItems(addedPayload) {
  const addedKeys = nwCollectCartPayloadKeys(addedPayload);
  if (!addedKeys.length) return;

  try {
    await nwUpdateCartKeysToZero(addedKeys);
  } catch (_) {}
}

function nwClearCartEditState() {
  window.NW_CART_EDIT_STATE = null;

  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('nw_cart_edit');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  } catch (_) {}
}

function nwFindCartEditAddons(cartItems, mainItem) {
  const props = mainItem.properties || {};
  const bundleId = props._bundle_id || '';
  const mainTitle = mainItem.product_title || mainItem.title || '';

  return (cartItems || []).filter(item => {
    if (!item || item.key === mainItem.key) return false;
    const itemProps = item.properties || {};
    const bundledWith = itemProps._bundled_with || '';
    if (!bundledWith) return false;

    const addonBundleId = itemProps._bundle_id || '';
    if (bundleId && addonBundleId) return addonBundleId === bundleId;
    return !bundleId && !addonBundleId && bundledWith === mainTitle;
  });
}

function nwSelectAddonVariantIds(addonPicker, addonVariantIds) {
  if (!addonPicker || !addonVariantIds || !addonVariantIds.length) return;

  const selectedIds = new Set(addonVariantIds.map(id => String(id)));

  addonPicker.groups.forEach(group => {
    const isMulti = group.dataset.nwAddonMulti === 'true';
    const swatches = Array.from(group.querySelectorAll('[data-nw-addon-option]'));
    let matched = false;

    if (isMulti) {
      swatches.forEach(sw => {
        const isSelected = selectedIds.has(String(sw.dataset.addonVariantId || ''));
        sw.classList.toggle('is-selected', isSelected);
        sw.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        if (isSelected) matched = true;
      });

      if (!matched) {
        const noneBtn = swatches.find(sw => sw.dataset.addonTitle === 'None');
        if (noneBtn) {
          noneBtn.classList.add('is-selected');
          noneBtn.setAttribute('aria-checked', 'true');
        }
      }
    } else {
      const target = swatches.find(sw => selectedIds.has(String(sw.dataset.addonVariantId || '')))
        || swatches.find(sw => sw.dataset.addonTitle === 'None')
        || swatches[0];

      swatches.forEach(sw => {
        const isSelected = sw === target;
        sw.classList.toggle('is-selected', isSelected);
        sw.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });
    }

    const selected = group.querySelector('[data-nw-addon-option].is-selected');
    if (selected) {
      addonPicker.updateGroupLabel(group, group.dataset.nwAddonGroup, selected);
    }
  });

  addonPicker.updateSummary();
}

async function nwLoadCartEditState(formEl, addonPicker, atcController) {
  const editKey = nwGetCartEditKey();
  if (!editKey) return;

  window.NW_CART_EDIT_STATE = { ready: false, editKey, keys: [editKey], addonVariantIds: [] };

  if (atcController?.btn) {
    atcController.btn.disabled = true;
    atcController._setBtnText('Loading configuration...', false);
  }

  try {
    const res = await fetch(nwCartEndpoint('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!res.ok) throw new Error('Unable to load cart item for editing');

    const cartData = await res.json();
    const cartItems = Array.isArray(cartData.items) ? cartData.items : [];
    const mainItem = cartItems.find(item => item.key === editKey);
    if (!mainItem) {
      window.NW_CART_EDIT_STATE = null;
      return;
    }

    const addons = nwFindCartEditAddons(cartItems, mainItem);
    const addonVariantIds = addons
      .map(item => item.variant_id || item.id)
      .filter(Boolean)
      .map(id => String(id));
    const keys = [mainItem.key].concat(addons.map(item => item.key).filter(Boolean));

    window.NW_CART_EDIT_STATE = {
      ready: true,
      editKey,
      keys,
      addonVariantIds
    };

    nwSelectAddonVariantIds(addonPicker, addonVariantIds);

    const qtyControl = formEl.querySelector('.nw-pdp-qty');
    if (qtyControl && mainItem.quantity) {
      qtyControl.dispatchEvent(new CustomEvent('nw:quantity:set', {
        detail: { quantity: mainItem.quantity }
      }));
    }

    if (atcController?.btn) {
      atcController.origLabel = 'Update Cart';
      atcController._setBtnText(atcController.origLabel, true);
    }
  } catch (error) {
    console.warn('NW cart edit load failed:', error);
    window.NW_CART_EDIT_STATE = null;
  } finally {
    if (atcController?.btn) {
      atcController.btn.disabled = false;
      if (!nwGetReadyCartEditState()) {
        atcController._setBtnText(atcController.origLabel, true);
      }
    }
  }
}

class NwAddToCart {
  constructor(formEl) {
    this.form   = formEl;
    this.btn    = formEl.querySelector('.nw-pdp-atc-btn');
    this.atcPriceSpan = this.btn?.querySelector('[data-nw-atc-price]');
    this.origLabel = 'Add to Cart';

    // Capture the text label without the price span
    if (this.btn) {
      const clone = this.btn.cloneNode(true);
      const priceInClone = clone.querySelector('[data-nw-atc-price]');
      if (priceInClone) priceInClone.remove();
      this.origLabel = clone.textContent.trim() || 'Add to Cart';
    }

    formEl.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!this.btn || this.btn.disabled) return;

      // Gift card recipient fields (only enabled when "Recipient's email" is chosen).
      // Validate here too: the sticky ATC bar dispatches submit without browser validation.
      const recipientForm = formEl.querySelector('gift-card-recipient-form');
      const recipientFields = recipientForm
        ? Array.from(recipientForm.querySelectorAll('input[name^="properties["], textarea[name^="properties["]'))
            .filter((field) => !field.disabled)
        : [];
      if (!recipientFields.every((field) => field.reportValidity())) return;

      this.btn.disabled    = true;
      this._setBtnText('Adding…', false);


      try {
        // ── Build the items array ──
        const items = [];

        // 1. Main product
        const variantId = formEl.querySelector('#nw-variant-id')?.value;
        const qtyEl     = formEl.querySelector('.nw-pdp-qty__val');
        const qty       = parseInt(qtyEl?.textContent, 10) || 1;
        const isFreight = formEl.querySelector('#nw-is-freight')?.value === 'true';

        if (!variantId) throw new Error('No variant selected');

        // Collect add-on names as a summary property on the main product
        const addonSummaryParts = [];
        const addonContainer    = formEl.querySelector('[data-nw-addons]');
        const editState = nwGetReadyCartEditState();


        // Generate a unique bundle ID so main + addon items are linked
        // even when the same product is added multiple times with different addons
        const bundleId = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);


        if (addonContainer) {
          addonContainer.querySelectorAll('[data-nw-addon-group]').forEach(group => {
            const isMulti = group.dataset.nwAddonMulti === 'true';
            // Multi-select groups can have multiple selected items; single-select has one
            const selectedItems = isMulti
              ? Array.from(group.querySelectorAll('[data-nw-addon-option].is-selected'))
              : [group.querySelector('[data-nw-addon-option].is-selected')].filter(Boolean);

            selectedItems.forEach(selected => {
              const title     = selected.dataset.addonTitle || '';
              const addonVarId = selected.dataset.addonVariantId || '';

              if (title && title !== 'None' && addonVarId) {
                // Read discount % from data attribute (set by Liquid from metaobjects)
                const discPct = nwFormatAddonDiscountPct(selected.dataset.addonDiscountPct);
                const discSource = selected.dataset.addonDiscountSource || '';
                const addonIsFreight = selected.dataset.addonIsFreight === 'true';

                const addonProps = /** @type {Record<string, string>} */ ({
                  '_bundled_with': formEl.closest('.nw-pdp-main')?.querySelector('.nw-pdp-title')?.textContent?.trim() || 'Main Product',
                  '_addon_discount_pct': discPct,
                  '_addon_discount_source': discSource,
                  '_bundle_id': bundleId
                });

                if (addonIsFreight) {
                  addonProps['_is_freight'] = 'true';
                }

                // Add add-on product as a separate cart item
                items.push({
                  id: parseInt(addonVarId, 10),
                  quantity: qty,
                  properties: addonProps
                });
                addonSummaryParts.push(title);
              }
            });
          });
        }

        // Build main product properties
        const mainProperties = {};
        const mainFeaturedImageEl = formEl.querySelector('#nw-product-featured-image');
        if (mainFeaturedImageEl && mainFeaturedImageEl.value) {
          mainProperties['_main_image'] = mainFeaturedImageEl.value;
        }
        if (addonSummaryParts.length > 0) {
          mainProperties['Selected Add-ons'] = addonSummaryParts.join(' | ');
          mainProperties['_bundle_id'] = bundleId;
        }
        if (editState && !mainProperties['_bundle_id']) {
          mainProperties['_bundle_id'] = bundleId;
        }
        // Shopify reads these to email the gift card to the recipient on the "Send on" date
        recipientFields.forEach((field) => {
          if (field.value) mainProperties[field.name.slice('properties['.length, -1)] = field.value;
        });
        if (isFreight) {
          mainProperties['_is_freight'] = 'true';
          const freightLabelEl = formEl.querySelector('#nw-freight-label');
          const freightPriceEl = formEl.querySelector('#nw-freight-price');
          if (freightLabelEl && freightLabelEl.value) {
            mainProperties['_freight_label'] = freightLabelEl.value;
          }
          if (freightPriceEl && freightPriceEl.value) {
            mainProperties['_freight_price'] = freightPriceEl.value;
          }
        }

        // Main product item (always first)
        items.unshift({
          id: parseInt(variantId, 10),
          quantity: qty,
          properties: mainProperties
        });

        // ── POST all items at once ──
        const res = await fetch(nwCartEndpoint('cart/add.js'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          if (recipientForm) {
            // Lets the recipient form show Shopify's per-field errors (email, send date, ...)
            document.dispatchEvent(new CustomEvent('cart:error', {
              detail: { data: { message: errData.message, errors: errData.errors, description: errData.description } }
            }));
          }
          throw new Error(errData.description || 'Cart error');
        }

        const addedData = await res.json().catch(() => ({}));

        if (editState) {
          try {
            await nwUpdateCartKeysToZero(editState.keys);
            nwClearCartEditState();
            this.origLabel = 'Add to Cart';
          } catch (replaceError) {
            await nwRollbackAddedCartItems(addedData);
            throw replaceError;
          }
        }
        
        
        this._setBtnText(editState ? 'Updated' : 'Added', false);
        this.btn.style.background = '#3d4e42';

          // Update cart count globally
        try {
          const cartRes  = await fetch(nwCartEndpoint('cart.js'), {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          });
          const cartData = await cartRes.json();
          document.dispatchEvent(new CustomEvent('cart:update', {
            detail: { data: { itemCount: cartData.item_count } }
          }));
          document.dispatchEvent(new CustomEvent('cart:updated'));
        } catch(_) {}
        // Always fire after a successful add (outside inner try so it can't be swallowed)
        document.dispatchEvent(new CustomEvent('cart:item-added'));
        setTimeout(() => {
          this.btn.disabled    = false;
          this._setBtnText(this.origLabel, true);
          this.btn.style.background = '';
        }, 2200);

      } catch (err) {
        console.warn('NW ATC Error:', err);
        this.btn.disabled    = false;
        this._setBtnText('Try Again', false);
        setTimeout(() => { this._setBtnText(this.origLabel, true); }, 2000);
      }
    });
  }

  /**
   * Set button text and optionally re-attach the price span.
   * @param {string} label  The text label (e.g. "Add to Cart", "Adding…")
   * @param {boolean} showPrice  Whether to append the price span
   */
  _setBtnText(label, showPrice) {
    if (!this.btn) return;
    this.btn.textContent = '';
    this.btn.append(label);
    if (showPrice && this.atcPriceSpan) {
      this.btn.appendChild(this.atcPriceSpan);
    }
  }
}


/* ────────────────────────────────────────────────────
   VIDEO MODAL — open/close, iframe creation
   ──────────────────────────────────────────────────── */
class NwVideoModal {
  constructor() {
    this.modal     = document.querySelector('[data-video-modal]');
    this.holder    = document.querySelector('[data-video-modal-holder]');
    this.closeBtn  = document.querySelector('[data-video-modal-close]');
    if (!this.modal || !this.holder) return;

    // Tile click → open
    document.querySelectorAll('.nw-pdp-video-tile').forEach(tile => {
      tile.addEventListener('click', () => {
        const url  = tile.dataset.videoUrl;
        const type = tile.dataset.videoType || 'external';
        if (!url) return;
        this.open(url, type);
      });
    });

    // Close handlers
    this.closeBtn?.addEventListener('click', () => this.close());
    this.modal.querySelector('[data-video-modal-backdrop]')?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) this.close();
    });
  }

  open(url, type) {
    this.holder.innerHTML = '';
    if (type === 'shopify_video') {
      const video = document.createElement('video');
      video.src = url;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      this.holder.appendChild(video);
    } else {
      // external — YouTube / Vimeo embed
      const embedUrl = this.toEmbed(url);
      const iframe = document.createElement('iframe');
      iframe.src = embedUrl;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.allowFullscreen = true;
      this.holder.appendChild(iframe);
    }
    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.modal.classList.remove('is-open');
    this.holder.innerHTML = '';
    document.body.style.overflow = '';
  }

  toEmbed(url) {
    // YouTube
    let m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]+)/);
    if (m) return 'https://www.youtube.com/embed/' + m[1] + '?autoplay=1&rel=0';
    // Vimeo
    m = url.match(/vimeo\.com\/(\d+)/);
    if (m) return 'https://player.vimeo.com/video/' + m[1] + '?autoplay=1';
    // Fallback: use URL as-is in iframe
    return url;
  }
}

/* ────────────────────────────────────────────────────
   MANUALS DROPDOWN — toggle hidden, aria-expanded
   ──────────────────────────────────────────────────── */
class NwManualsDropdown {
  constructor(el) {
    this.btn = el.querySelector('.nw-pdp-manuals__btn');
    this.dd  = el.querySelector('.nw-pdp-manuals__dd');
    if (!this.btn || !this.dd) return;

    this.btn.addEventListener('click', () => this.toggle());
  }

  toggle() {
    const open = this.dd.hasAttribute('hidden');
    if (open) {
      this.dd.removeAttribute('hidden');
      this.btn.setAttribute('aria-expanded', 'true');
    } else {
      this.dd.setAttribute('hidden', '');
      this.btn.setAttribute('aria-expanded', 'false');
    }
  }
}

/* ────────────────────────────────────────────────────
   ADD-ON SWATCH PICKER — metaobject-driven groups
   Handles selection, subtotal, summary, line items
   ──────────────────────────────────────────────────── */
class NwAddonPicker {
  constructor(container, formEl) {
    this.container   = container;
    this.formEl      = formEl;
    this.basePrice   = parseInt(container.dataset.basePrice, 10) || 0;
    this.groups      = container.querySelectorAll('[data-nw-addon-group]');
    this.summaryEl   = container.querySelector('[data-nw-addon-summary]');
    this.summaryLines = container.querySelector('[data-nw-addon-summary-lines]');
    this.subtotalEl  = container.querySelector('[data-nw-addon-subtotal]');
    this.atcPriceEl  = formEl.querySelector('[data-nw-atc-price]');

    if (!this.groups.length) return;

    // Bind click events on every swatch button
    this.groups.forEach(group => {
      const groupIdx  = group.dataset.nwAddonGroup;
      const swatches  = group.querySelectorAll('[data-nw-addon-option]');

      swatches.forEach(sw => {
        sw.addEventListener('click', () => {
          const isMulti = group.dataset.nwAddonMulti === 'true';

          if (isMulti) {
            const noneBtn = group.querySelector('[data-nw-addon-option][data-addon-title="None"]');

            if (sw.dataset.addonTitle === 'None') {
              // Clicking None: clear all selections, select None
              swatches.forEach(s => { s.classList.remove('is-selected'); s.setAttribute('aria-checked', 'false'); });
              sw.classList.add('is-selected');
              sw.setAttribute('aria-checked', 'true');
            } else {
              // Toggle this item; always deselect None first
              if (noneBtn) { noneBtn.classList.remove('is-selected'); noneBtn.setAttribute('aria-checked', 'false'); }
              if (sw.classList.contains('is-selected')) {
                sw.classList.remove('is-selected');
                sw.setAttribute('aria-checked', 'false');
              } else {
                sw.classList.add('is-selected');
                sw.setAttribute('aria-checked', 'true');
              }
              // If nothing is now selected and None exists, restore None
              const anySelected = Array.from(swatches).some(s => s.classList.contains('is-selected'));
              if (!anySelected && noneBtn) { noneBtn.classList.add('is-selected'); noneBtn.setAttribute('aria-checked', 'true'); }
            }
          } else {
            // Single-select (radio) behaviour
            swatches.forEach(s => { s.classList.remove('is-selected'); s.setAttribute('aria-checked', 'false'); });
            sw.classList.add('is-selected');
            sw.setAttribute('aria-checked', 'true');
          }

          this.updateGroupLabel(group, groupIdx, sw);
          this.updateSummary();
        });
      });
    });

// Show labels for any pre-selected swatches on page load
    this.groups.forEach(group => {
      const groupIdx   = group.dataset.nwAddonGroup;
      const preSelected = group.querySelector('[data-nw-addon-option].is-selected');
      if (preSelected) {
        this.updateGroupLabel(group, groupIdx, preSelected);
      }
    });

    // Initial summary render
    this.updateSummary();
  }

  /* ── Format cents → $X,XXX.XX ── */
  fmt(cents) {
    const amount = (cents / 100).toFixed(2);
    const parts  = amount.split('.');
    const intPart = Number(parts[0]).toLocaleString();
    return `$${intPart}.${parts[1]}`;
  }

  /* ── Update the selected label(s) under a group's swatches ── */
  updateGroupLabel(group, groupIdx, sw) {
    const labelEl = group.querySelector(`[data-nw-addon-selected-label="${groupIdx}"]`);
    if (!labelEl) return;

    if (group.dataset.nwAddonMulti === 'true') {
      // Multi-select: one text line per selected non-None item
      const selected = Array.from(group.querySelectorAll('[data-nw-addon-option].is-selected'))
        .filter(s => s.dataset.addonTitle !== 'None' && s.dataset.addonTitle !== '');
      if (!selected.length) { labelEl.innerHTML = ''; return; }
      labelEl.innerHTML = selected.map(s => {
        const p  = parseInt(s.dataset.addonPrice, 10) || 0;
        const op = parseInt(s.dataset.addonOriginalPrice, 10) || 0;
        const dp = nwClampAddonDiscountPct(s.dataset.addonDisplayDiscountPct ?? s.dataset.addonDiscountPct);
        const t  = s.dataset.addonTitle || '';
        const url = s.dataset.addonProductUrl || '';
        const tHtml = url ? `<a href="${url}" target="_blank" class="nw-pdp-addon-link">${t}</a>` : t;
        if (dp > 0 && op > p) {
          return `<span class="nw-pdp-addon-label-line">${tHtml} (<span class="nw-pdp-addon-price-original">${this.fmt(op)}</span> <span class="nw-pdp-addon-price-highlight">+${this.fmt(p)}</span>)</span>`;
        }
        return `<span class="nw-pdp-addon-label-line">${tHtml} (<span class="nw-pdp-addon-price-highlight">+${this.fmt(p)}</span>)</span>`;
      }).join('');
      return;
    }

    // Single-select: original behaviour
    const price    = parseInt(sw.dataset.addonPrice, 10) || 0;
    const origPrice = parseInt(sw.dataset.addonOriginalPrice, 10) || 0;
    const discPct  = nwClampAddonDiscountPct(sw.dataset.addonDisplayDiscountPct ?? sw.dataset.addonDiscountPct);
    const title    = sw.dataset.addonTitle || '';
    const url      = sw.dataset.addonProductUrl || '';
    const titleHtml = url ? `<a href="${url}" target="_blank" class="nw-pdp-addon-link">${title}</a>` : title;

    if (title === 'None' || title === '') {
      labelEl.innerHTML = '';
    } else if (discPct > 0 && origPrice > price) {
      labelEl.innerHTML = `${titleHtml} (<span class="nw-pdp-addon-price-original">${this.fmt(origPrice)}</span> <span class="nw-pdp-addon-price-highlight">+${this.fmt(price)}</span>)`;
    } else {
      labelEl.innerHTML = `${titleHtml} (<span class="nw-pdp-addon-price-highlight">+${this.fmt(price)}</span>)`;
    }
  }

  /* ── Rebuild the subtotal summary section ── */
  updateSummary() {
    let totalAddons = 0;
    let hasAny      = false;
    let linesHtml   = '';

    this.groups.forEach(group => {
      // Support multi-select: collect all selected items; for single-select get one
      const isMulti = group.dataset.nwAddonMulti === 'true';
      const selectedItems = isMulti
        ? Array.from(group.querySelectorAll('[data-nw-addon-option].is-selected'))
        : [group.querySelector('[data-nw-addon-option].is-selected')].filter(Boolean);

      selectedItems.forEach(selected => {
        const price     = parseInt(selected.dataset.addonPrice, 10) || 0;
        const origPrice = parseInt(selected.dataset.addonOriginalPrice, 10) || 0;
        const discPct   = nwClampAddonDiscountPct(selected.dataset.addonDisplayDiscountPct ?? selected.dataset.addonDiscountPct);
        const title     = selected.dataset.addonTitle || '';
        const url       = selected.dataset.addonProductUrl || '';
        const titleHtml = url ? `<a href="${url}" target="_blank" class="nw-pdp-addon-link">${title}</a>` : title;

        if (title !== 'None' && title !== '' && price > 0) {
          hasAny = true;
          totalAddons += price;

          if (discPct > 0 && origPrice > price) {
            linesHtml += `
            <div class="nw-pdp-addon-summary__line">
              <span class="nw-pdp-addon-summary__line-label">${titleHtml} <span class="nw-pdp-addon-discount-badge">-${discPct}%</span></span>
              <span class="nw-pdp-addon-summary__line-price"><span class="nw-pdp-addon-price-original">${this.fmt(origPrice)}</span> +${this.fmt(price)}</span>
            </div>`;
          } else {
            linesHtml += `
            <div class="nw-pdp-addon-summary__line">
              <span class="nw-pdp-addon-summary__line-label">${titleHtml}</span>
              <span class="nw-pdp-addon-summary__line-price">+${this.fmt(price)}</span>
            </div>`;
          }
        }
      });
    });

    const grandTotal = this.basePrice + totalAddons;

    if (hasAny && this.summaryEl) {
      // Add base product line at top
      const baseLine = `
        <div class="nw-pdp-addon-summary__line">
          <span class="nw-pdp-addon-summary__line-label">Product Base Price</span>
          <span class="nw-pdp-addon-summary__line-price">${this.fmt(this.basePrice)}</span>
        </div>`;
      this.summaryLines.innerHTML = baseLine + linesHtml;
      this.subtotalEl.textContent = this.fmt(grandTotal);
      this.summaryEl.style.display = '';
    } else if (this.summaryEl) {
      this.summaryLines.innerHTML = '';
      this.summaryEl.style.display = 'none';
    }

    // Update ATC button price
    if (this.atcPriceEl) {
      this.atcPriceEl.innerHTML = ' <span class="nw-pdp-atc-sep">&middot;</span> ' + this.fmt(grandTotal);
    }

    // Update financing price
    if (window.nwAffirmController) {
      window.nwAffirmController.updatePrice(grandTotal);
    }
    if (window.nwShopPayController) {
      window.nwShopPayController.updatePrice(grandTotal, this.basePrice);
    }
  }

  /* ── Sync base price when variant changes ── */
  syncBasePrice(newPrice) {
    this.basePrice = newPrice;
    this.container.dataset.basePrice = newPrice;
    this.updateSummary();
  }
}

/* Shop Pay Financing Controller */
class NwShopPayController {
  constructor() {
    this.terms = Array.from(document.querySelectorAll('[data-nw-shop-pay-terms]'));
    this.moneyPattern = /\$[\d,]+(?:\.\d{2})?/;

    if (this.terms.length === 0) return;

    this.terms.forEach(term => {
      const baseAmount = this.toCents(term.dataset.baseAmount);
      term.dataset.nwShopPayBaseAmount = String(baseAmount);
      term.dataset.currentAmount = String(baseAmount);

      this.observeTerm(term);
      this.queueRefresh(term);
      [250, 1000].forEach(delay => {
        this.queueRefresh(term, delay);
      });
    });
  }

  toCents(value) {
    const amount = parseInt(value, 10);
    return Number.isFinite(amount) ? amount : 0;
  }

  parseMoneyToCents(value) {
    const cleanValue = String(value || '').replace(/[^0-9.]/g, '');
    const amount = parseFloat(cleanValue);
    return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
  }

  formatMoney(cents) {
    const amount = Math.max(0, cents) / 100;
    return `$${amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  observeTerm(term) {
    const observer = new MutationObserver(() => {
      if (term.dataset.nwShopPayUpdating === 'true') return;
      this.queueRefresh(term);
    });

    observer.observe(term, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  queueRefresh(term, delay) {
    if (delay) {
      window.setTimeout(() => this.queueRefresh(term), delay);
      return;
    }

    if (term.dataset.nwShopPayRefreshQueued === 'true') return;

    term.dataset.nwShopPayRefreshQueued = 'true';

    window.requestAnimationFrame(() => {
      term.dataset.nwShopPayRefreshQueued = 'false';
      this.refreshTerm(term);
    });
  }

  refreshTerm(term) {
    this.captureNativeMonthly(term);
    const currentAmount = this.toCents(term.dataset.currentAmount) || this.toCents(term.dataset.baseAmount);
    this.renderAdjustedMonthly(term, currentAmount);
  }

  captureNativeMonthly(term) {
    const textNode = this.findMoneyTextNode(term);
    if (!textNode) return;

    const text = textNode.nodeValue || '';
    if (text === term.dataset.nwShopPayRenderedText) return;

    const match = text.match(this.moneyPattern);
    if (!match) return;

    const monthlyCents = this.parseMoneyToCents(match[0]);
    if (!monthlyCents) return;

    const baseAmount = this.toCents(term.dataset.baseAmount)
      || this.toCents(term.dataset.nwShopPayBaseAmount)
      || this.toCents(term.dataset.currentAmount);

    term.dataset.nwShopPayMonthlyCents = String(monthlyCents);
    term.dataset.nwShopPayBaseAmount = String(baseAmount);
    term.dataset.nwShopPayNativeText = text;
  }

  findMoneyTextNode(root) {
    const node = this.findMoneyTextNodeInRoot(root);
    if (node) return node;

    const paymentTerms = root.querySelector ? root.querySelector('shopify-payment-terms') : null;

    if (paymentTerms && paymentTerms.shadowRoot) {
      return this.findMoneyTextNodeInRoot(paymentTerms.shadowRoot);
    }

    return null;
  }

  findMoneyTextNodeInRoot(root) {
    if (!root || !window.NodeFilter) return null;

    const walker = document.createTreeWalker(root, window.NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();

    while (node) {
      if (this.moneyPattern.test(node.nodeValue || '')) {
        return node;
      }
      node = walker.nextNode();
    }

    return null;
  }

  renderAdjustedMonthly(term, priceInCents) {
    const nativeMonthlyCents = this.toCents(term.dataset.nwShopPayMonthlyCents);
    const baseAmount = this.toCents(term.dataset.nwShopPayBaseAmount) || this.toCents(term.dataset.baseAmount);
    const textNode = this.findMoneyTextNode(term);

    if (!nativeMonthlyCents || !baseAmount || !priceInCents || !textNode) return;

    const adjustedMonthlyCents = Math.max(1, Math.round((nativeMonthlyCents * priceInCents) / baseAmount));
    const text = textNode.nodeValue || '';
    const updatedText = text.replace(this.moneyPattern, this.formatMoney(adjustedMonthlyCents));

    if (updatedText === text) return;

    term.dataset.nwShopPayUpdating = 'true';
    textNode.nodeValue = updatedText;
    term.dataset.nwShopPayRenderedText = updatedText;

    window.setTimeout(() => {
      term.dataset.nwShopPayUpdating = 'false';
    }, 0);
  }

  updatePrice(priceInCents, baseAmount) {
    this.terms.forEach(term => {
      const normalizedPrice = this.toCents(priceInCents);
      const normalizedBase = this.toCents(baseAmount);
      const previousBase = this.toCents(term.dataset.nwShopPayBaseAmount);
      const baseChanged = Boolean(normalizedBase && previousBase && normalizedBase !== previousBase);

      if (normalizedBase) {
        term.dataset.baseAmount = String(normalizedBase);
        term.dataset.nwShopPayBaseAmount = String(normalizedBase);
      }

      term.dataset.currentAmount = String(normalizedPrice);

      if (baseChanged) {
        delete term.dataset.nwShopPayMonthlyCents;
        [150, 600].forEach(delay => {
          this.queueRefresh(term, delay);
        });
      }

      if (baseChanged && normalizedPrice === normalizedBase) return;

      this.renderAdjustedMonthly(term, normalizedPrice);
    });
  }
}

/* Affirm Financing Controller */
class NwAffirmController {
  constructor() {
    this.promos = Array.from(document.querySelectorAll('.nw-affirm-promo .affirm-as-low-as'));
    this.refreshTimer = null;
    this.refreshQueued = false;
    this.clickHoldTimer = null;
    this.isUserInteracting = false;

    this.bindTriggerGuard();

    if (this.promos.length === 0) return;

    this.promos.forEach(promo => {
      const observer = new MutationObserver(() => {
        this.queueCustomize(promo);
      });

      observer.observe(promo, { childList: true, subtree: true });
      this.queueCustomize(promo);
    });
  }

  bindTriggerGuard() {
    document.addEventListener('pointerdown', (event) => {
      const trigger = this.getAffirmTrigger(event.target);
      if (!trigger) return;

      this.holdRefreshDuringClick();
      this.neutralizeUnsafeHref(trigger);
    }, true);

    document.addEventListener('click', (event) => {
      const trigger = this.getAffirmTrigger(event.target);
      if (!trigger) return;

      this.holdRefreshDuringClick();
      this.neutralizeUnsafeHref(trigger);
    }, true);
  }

  getAffirmTrigger(target) {
    if (!(target instanceof Element)) return null;

    const trigger = target.closest('.nw-affirm-promo a, .affirm-modal-trigger');
    if (!(trigger instanceof HTMLAnchorElement)) return null;
    if (!trigger.closest('.nw-affirm-promo')) return null;

    return trigger;
  }

  holdRefreshDuringClick() {
    this.isUserInteracting = true;
    window.clearTimeout(this.clickHoldTimer);

    this.clickHoldTimer = window.setTimeout(() => {
      this.isUserInteracting = false;

      if (this.refreshQueued) {
        this.runRefresh();
      }
    }, 1200);
  }

  hasUnsafeFallbackHref(link) {
    const href = link.getAttribute('href');
    if (!href || href === '#') return true;

    if (/^\s*javascript:/i.test(href)) return false;

    try {
      const url = new URL(href, window.location.href);
      return url.origin === window.location.origin && url.pathname === window.location.pathname;
    } catch (e) {
      return false;
    }
  }

  neutralizeUnsafeHref(link) {
    if (!this.hasUnsafeFallbackHref(link)) return;

    if (!link.dataset.nwAffirmOriginalHref) {
      link.dataset.nwAffirmOriginalHref = link.getAttribute('href') || '';
    }

    link.setAttribute('href', 'javascript:void(0)');
  }

  queueCustomize(promoElement) {
    if (!promoElement || promoElement.dataset.nwAffirmCustomizeQueued === 'true') return;

    promoElement.dataset.nwAffirmCustomizeQueued = 'true';

    window.requestAnimationFrame(() => {
      promoElement.dataset.nwAffirmCustomizeQueued = 'false';
      this.customizeLinkText(promoElement);
    });
  }

  customizeLinkText(promoElement) {
    // 1. Hide the 0% APR badge / tag
    const badges = promoElement.querySelectorAll('.affirm-ala-badge, .affirm-badge, [class*="badge"], [class*="apr"]');
    badges.forEach(badge => {
      badge.style.setProperty('display', 'none', 'important');
    });

    const possibleBadges = promoElement.querySelectorAll('span, div, sup');
    possibleBadges.forEach(child => {
      const txt = child.textContent.trim();
      if ((txt.includes('0%') || txt.includes('APR') || txt.toLowerCase().includes('apr')) && child.children.length === 0) {
        child.style.setProperty('display', 'none', 'important');

        const parent = child.parentElement;
        if (parent && parent !== promoElement && (parent.className.includes('badge') || parent.className.includes('apr') || parent.className.includes('ala'))) {
          parent.style.setProperty('display', 'none', 'important');
        }
      }
    });

    // 2. Customize links
    const links = promoElement.querySelectorAll('a');
    links.forEach(link => {
      const txt = link.textContent.trim();
      if (txt.toLowerCase() === 'learn more' && link.dataset.nwAffirmLabelUpdated !== 'true') {
        link.textContent = 'See your options';
        link.dataset.nwAffirmLabelUpdated = 'true';
      }

      this.neutralizeUnsafeHref(link);

      if (link.dataset.nwAffirmStyled !== 'true') {
        link.style.textDecoration = 'underline';
        link.style.fontWeight = '500';
        link.style.color = '#000';
        link.dataset.nwAffirmStyled = 'true';
      }
    });
  }

  queueRefresh() {
    this.refreshQueued = true;
    window.clearTimeout(this.refreshTimer);

    this.refreshTimer = window.setTimeout(() => {
      if (this.isUserInteracting) return;
      this.runRefresh();
    }, 120);
  }

  runRefresh() {
    if (this.isUserInteracting) return;

    this.refreshQueued = false;

    if (window.affirm && window.affirm.ui && typeof window.affirm.ui.refresh === 'function') {
      window.affirm.ui.refresh();
    }
  }

  updatePrice(priceInCents) {
    let didChange = false;

    this.promos.forEach(promo => {
      const amount = String(priceInCents);

      if (promo.getAttribute('data-amount') !== amount) {
        promo.setAttribute('data-amount', amount);
        didChange = true;
      }
    });

    if (!didChange) return;

    this.queueRefresh();
  }
}

/* ────────────────────────────────────────────────────
   BOOT — initialise everything when DOM is ready
   Each component is wrapped in try/catch so a single
   failure never blocks the rest (especially animations).
   ──────────────────────────────────────────────────── */
function nwInitLaunchCountdowns() {
  const countdowns = document.querySelectorAll('[data-nw-launch-countdown]');
  if (!countdowns.length) return;

  const pad = (value) => String(value).padStart(2, '0');

  countdowns.forEach((countdown) => {
    if (countdown.dataset.nwLaunchCountdownReady === 'true') return;

    const endValue = countdown.getAttribute('data-countdown-end');
    const endTime = endValue ? new Date(endValue).getTime() : NaN;
    const daysEl = countdown.querySelector('[data-countdown-days]');
    const hoursEl = countdown.querySelector('[data-countdown-hours]');
    const minutesEl = countdown.querySelector('[data-countdown-minutes]');
    const secondsEl = countdown.querySelector('[data-countdown-seconds]');

    if (!Number.isFinite(endTime) || !daysEl || !hoursEl || !minutesEl || !secondsEl) {
      countdown.hidden = true;
      countdown.setAttribute('hidden', '');
      countdown.style.display = 'none';
      countdown.remove();
      return;
    }

    if (countdown._nwTimer) {
      window.clearInterval(countdown._nwTimer);
      countdown._nwTimer = null;
    }

    const render = () => {
      const remaining = endTime - Date.now();

      if (remaining <= 0) {
        if (countdown._nwTimer) {
          window.clearInterval(countdown._nwTimer);
          countdown._nwTimer = null;
        }
        countdown.hidden = true;
        countdown.setAttribute('hidden', '');
        countdown.style.display = 'none';
        countdown.remove();
        return;
      }

      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      daysEl.textContent = String(days);
      hoursEl.textContent = pad(hours);
      minutesEl.textContent = pad(minutes);
      secondsEl.textContent = pad(seconds);
      countdown.removeAttribute('hidden');
      countdown.hidden = false;
    };

    countdown.dataset.nwLaunchCountdownReady = 'true';
    render();
    if (countdown.isConnected && !countdown.hidden) {
      countdown._nwTimer = window.setInterval(render, 1000);
    }
  });
}

function nwRunLaunchCountdowns() {
  try {
    nwInitLaunchCountdowns();
  } catch (e) {
    console.warn('NW: LaunchCountdown failed', e);
  }
}

// Resilient initialization: execute immediately if DOM is ready, otherwise wait for DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', nwRunLaunchCountdowns);
} else {
  nwRunLaunchCountdowns();
}

// Re-initialize on Shopify Theme Editor section load/refresh
document.addEventListener('shopify:section:load', (e) => {
  if (e.target && typeof e.target.querySelectorAll === 'function') {
    e.target.querySelectorAll('[data-nw-launch-countdown]').forEach((el) => {
      delete el.dataset.nwLaunchCountdownReady;
    });
  }
  nwRunLaunchCountdowns();
});

document.addEventListener('DOMContentLoaded', () => {

  // Sauna Elite launch price countdown
  nwRunLaunchCountdowns();

  // Initialize Shop Pay Controller first
  try {
    window.nwShopPayController = new NwShopPayController();
  } catch(e) {
    console.warn('NW: ShopPayController failed', e);
  }

  // Initialize Affirm Controller first
  try {
    window.nwAffirmController = new NwAffirmController();
  } catch(e) {
    console.warn('NW: AffirmController failed', e);
  }

  // Scroll animations — run FIRST so content becomes visible even if other JS fails
  try { new NwScrollAnim(); } catch(e) { console.warn('NW: ScrollAnim failed', e); }

  // Gallery
  try {
    document.querySelectorAll('.nw-pdp-gallery').forEach(el => {
      const g = new NwGallery(el);
      el._nwGalleryInstance = g;
    });
  } catch(e) { console.warn('NW: Gallery failed', e); }

  // Initialize mobile duplicate image to match main gallery
  try {
    const mainImg = document.querySelector('.nw-pdp-gallery__main-img');
    const mobileImg = document.getElementById('nw-mobile-main-img');
    if (mainImg && mobileImg) {
      mobileImg.src = mainImg.src;
      mobileImg.alt = mainImg.alt;
    }
  } catch(e) { console.warn('NW: Mobile image sync failed', e); }

  // Product Accordion
  try { document.querySelectorAll('.nw-pdp-accordion').forEach(el => new NwAccordion(el)); } catch(e) { console.warn('NW: Accordion failed', e); }
 
  // Resources & Guides Jump Link
  try { initResourcesJumpLink(); } catch(e) { console.warn('NW: Resources jump link failed', e); }
 
  // Product Accordion Zoom (Delegated globally to handle Shopify theme editor re-renders)
  try {
    new NwAccordionZoom(document.body);
  } catch (e) {
    console.warn("NW: Accordion Zoom failed", e);
  }

  // FAQ
  try { document.querySelectorAll('.nw-pdp-faq').forEach(el => new NwFaq(el)); } catch(e) { console.warn('NW: FAQ failed', e); }

  // Qty
  try { document.querySelectorAll('.nw-pdp-qty').forEach(el => new NwQty(el)); } catch(e) { console.warn('NW: Qty failed', e); }

  // Variant Picker / Swatches + Add-on Picker
  try {
    const pdpForm = document.getElementById('nw-product-form');
    let addonPicker = null;

    if (pdpForm) {
      const vp = new NwVariantPicker(pdpForm);

      // Add-on Swatch Picker
      const addonContainer = pdpForm.querySelector('[data-nw-addons]');
      if (addonContainer) {
        addonPicker = new NwAddonPicker(addonContainer, pdpForm);

        // Patch NwVariantPicker.syncVariant to also update addon base price
        const origSync = vp.syncVariant.bind(vp);
        vp.syncVariant = function() {
          origSync();
          // After variant change, update addon base price
          const match = vp.variants.find(v =>
            Object.entries(vp.selected).every(([pos, val]) => v.options[parseInt(pos) - 1] === val)
          );
          if (match && addonPicker) {
            addonPicker.syncBasePrice(match.price);
          }
        };
      }

      const atcController = new NwAddToCart(pdpForm);
      nwLoadCartEditState(pdpForm, addonPicker, atcController);
      // ── Sticky ATC override for addon products ──────────────────────
      // The sticky "Add to Cart" bar must submit the NW form instead of
      // its standalone fallback form so quantity, add-ons, freight
      // properties, cart events, and drawer behavior stay in sync.
      if (pdpForm) {
        // Target both the theme's native sticky-add-to-cart and our custom nw-sticky-atc
        const stickyBar = document.querySelector('sticky-add-to-cart') || document.querySelector('nw-sticky-atc');
        const stickyBtn = stickyBar && stickyBar.querySelector('[ref="addToCartButton"]');
        if (stickyBtn) {
          stickyBtn.addEventListener('click', function(e) {
            const isKlaviyoTrigger = stickyBtn.classList.contains('klaviyo_form_trigger');
            const isPreorderSticky = stickyBar?.dataset?.isPreorder === 'true';
            if (isKlaviyoTrigger || isPreorderSticky) return;

            e.stopImmediatePropagation(); // prevent the sticky bar's own handler
            e.preventDefault();
            // Trigger the NW form submit (handles addons + dispatch events)
            pdpForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }, true); // capture phase → fires before any bubble-phase handlers
        }
      }
    }
  } catch(e) { console.warn('NW: VariantPicker/Addons/ATC failed', e); }

  // Star Rating
  try { document.querySelectorAll('.nw-pdp-review-form-box').forEach(el => new NwStarRating(el)); } catch(e) { console.warn('NW: StarRating failed', e); }

  // Judge.me Preview Badge Text Override
  try {
    const badges = document.querySelectorAll('.jdgm-preview-badge');
    badges.forEach(badge => {
      const observer = new MutationObserver(() => {
        const rating = badge.querySelector('.jdgm-prev-badge')?.getAttribute('data-average-rating');
        const count = badge.querySelector('.jdgm-prev-badge')?.getAttribute('data-number-of-reviews');
        const textEl = badge.querySelector('.jdgm-prev-badge__text');
        if (rating && count && textEl) {
          const formattedRating = parseFloat(rating).toFixed(1);
          const reviewsWord = parseInt(count) === 1 ? 'review' : 'reviews';
          const newText = `${formattedRating} stars · ${count} ${reviewsWord}`;
          if (textEl.textContent !== newText) {
            textEl.textContent = newText;
          }
        }
      });
      observer.observe(badge, { childList: true, subtree: true });
      
      // Try immediate sync
      const rating = badge.querySelector('.jdgm-prev-badge')?.getAttribute('data-average-rating');
      const count = badge.querySelector('.jdgm-prev-badge')?.getAttribute('data-number-of-reviews');
      const textEl = badge.querySelector('.jdgm-prev-badge__text');
      if (rating && count && textEl) {
        const formattedRating = parseFloat(rating).toFixed(1);
        const reviewsWord = parseInt(count) === 1 ? 'review' : 'reviews';
        textEl.textContent = `${formattedRating} stars · ${count} ${reviewsWord}`;
      }
    });
  } catch(e) { console.warn('NW: Judgeme override failed', e); }

  // Stories Slider
  try { document.querySelectorAll('.nw-pdp-stories-section').forEach(el => new NwStoriesSlider(el)); } catch(e) { console.warn('NW: Stories failed', e); }

  // Related slider
  try { document.querySelectorAll('.nw-pdp-related-section').forEach(el => new NwRelatedSlider(el)); } catch(e) { console.warn('NW: Related failed', e); }

  // Video Modal
  try { new NwVideoModal(); } catch(e) { console.warn('NW: VideoModal failed', e); }

  // Manuals Dropdown
  try { document.querySelectorAll('.nw-pdp-manuals').forEach(el => new NwManualsDropdown(el)); } catch(e) { console.warn('NW: Manuals failed', e); }

  
  // Gale HSA/FSA: "Learn more" opens the on-page modal; only "See if you qualify" goes to Gale.
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('.gale-learn-more') : null;
      if (!trigger) return;

      event.preventDefault();
      event.stopPropagation();

      const modal = document.getElementById('with-gale-modal');
      if (modal) modal.style.display = 'flex';
    },
    true
  );

  // Judge.me Preview Badge scroll click fallback
  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('.jdgm-preview-badge') : null;
    if (!trigger) return;

    const widget = document.querySelector('.jdgm-review-widget') || document.querySelector('.jdgm-widget') || document.getElementById('judgeme_product_reviews');
    if (widget) {
      event.preventDefault();
      event.stopPropagation();
      widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  // End Gallery touch swipe for mobile
});







document.addEventListener('DOMContentLoaded', function () {

  const gallery = document.querySelector('.nw-pdp-gallery__main');
  const zoomBtn = document.querySelector('.nw-pdp-gallery__zoom');

  if (!gallery || !zoomBtn) return;

  gallery.style.cursor = 'zoom-in';

  // poori gallery clickable
  gallery.onclick = function (e) {

    // arrows / zoom button / video ignore
    if (
      e.target.closest('.nw-pdp-gallery__nav-btn') ||
      e.target.closest('.nw-pdp-gallery__zoom') ||
      e.target.closest('.nw-pdp-gallery__play-overlay') ||
      e.target.closest('video')
    ) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    zoomBtn.click();
  };

});
